import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../components/AuthContext';
import api from '../../api';
import { jsPDF } from 'jspdf';
import { FaFileInvoice, FaFileAlt, FaTruck, FaChartLine, FaChartBar, FaBox, FaCheckCircle, FaClock, FaTimesCircle, FaCalendarAlt, FaRupeeSign, FaPrint, FaSearch, FaTimes } from 'react-icons/fa';
import EChart from '../../components/charts/EChart';
import { normalizeOrder, normalizeChartOrder, DASHBOARD_CHARTS } from '../../utils/dashboardSections';
import LazyMount from '../../components/charts/LazyMount';
import { KpiCard, ChartCard, EmptyState, StatusPill, TrendCard, SearchInput, SegmentedTabs } from '../../components/dashboard/DashboardPrimitives';
import { MODULE_BY_KEY, moduleAccessFlags, moduleRoute, moduleLabel } from '../../utils/dashboardModules';
import { statusPillClass, statusHex, MODULE_COLORS } from '../../utils/theme';
import {
  lineOption,
  barOption,
  pieOption,
  radialBarOption,
} from '../../components/charts/chartOptions';

// Compact-density overrides applied only inside the dashboard root.
const densityCSS = `
.dash-root.dash-compact .dash-section { margin-bottom: 1rem !important; }
.dash-root.dash-compact .dash-section .rounded-xl { padding: 0.75rem !important; }
.dash-root.dash-compact .dash-section .rounded-lg { padding: 0.75rem !important; }
/* Dark-mode shim for the item table in the record view modal, which uses
   hardcoded light utility classes. Scoped to .items-dark only. */
.items-dark .bg-white { background-color: #1f2937 !important; }
.items-dark .bg-gray-50 { background-color: #374151 !important; }
.items-dark .bg-gray-100 { background-color: #374151 !important; }
.items-dark .hover\\:bg-gray-50:hover { background-color: #374151 !important; }
.items-dark .text-gray-900 { color: #f3f4f6 !important; }
.items-dark .text-gray-800 { color: #e5e7eb !important; }
.items-dark .text-gray-700 { color: #e5e7eb !important; }
.items-dark .text-gray-600 { color: #d1d5db !important; }
.items-dark .text-gray-500 { color: #9ca3af !important; }
.items-dark .text-blue-700 { color: #93c5fd !important; }
.items-dark .border-gray-200 { border-color: #4b5563 !important; }
.items-dark .border-gray-300 { border-color: #4b5563 !important; }
.items-dark .divide-gray-100 > :not([hidden]) ~ :not([hidden]) { border-color: #374151 !important; }
.items-dark .divide-gray-200 > :not([hidden]) ~ :not([hidden]) { border-color: #4b5563 !important; }
`;

// Statuses shown in the personalized "My Performance" cards. Rendered in a
// fixed order with zeros so every module shows its status breakdown.
const PERSONAL_STATUS_SETS = {
  deliveryOrders: [
    { key: 'Pending', label: 'Pending' },
    { key: 'Completed', label: 'Completed' },
    { key: 'Cancelled', label: 'Cancelled' },
  ],
  quotationNew: [
    { key: 'PENDING', label: 'Pending' },
    { key: 'COMPLETED', label: 'Completed' },
    { key: 'PARTIAL-COMPLETE', label: 'Partial Complete' },
    { key: 'PARTIALLY-CLOSED', label: 'Partially Closed' },
    { key: 'REFFOLLOW', label: 'Ref Follow' },
    { key: 'CANCELLED', label: 'Cancelled' },
  ],
};
PERSONAL_STATUS_SETS.tallyQuotation = PERSONAL_STATUS_SETS.quotationNew;


// Helper function to format numbers with commas
const formatNumberWithCommas = (number) => {  // Handle null, undefined, or string values
  const value = typeof number === 'string' ? parseFloat(number) : number;
  
  // Return 0.00 if the value is not a valid number
  if (!value || isNaN(value)) {
    return '0.00';
  }

  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  }).format(value);
};

// Helper to safely format a number with toLocaleString
const safeNumber = (value) => {
  const num = typeof value === 'number' ? value : parseFloat(value);
  return isNaN(num) ? '0' : num.toLocaleString();
};

const isCoverageUnit = (unit) => {
    const normalizedUnit = String(unit || '').toLowerCase().replace(/[.\s]/g, '');
    return normalizedUnit.startsWith('sq');
};

const formatReservedQuantityWithArea = ({
    reservedByUnit,
    totalReservedQuantity,
    unit = 'pcs',
    totalReservedCoverageArea,
    coverageAreaUnit = 'sq.ft'
}) => {
    const entries = Object.entries(reservedByUnit || {}).filter(([, qty]) => (parseFloat(qty) || 0) > 0);
    const quantityEntries = entries.filter(([entryUnit]) => !isCoverageUnit(entryUnit));
    const areaEntries = entries.filter(([entryUnit]) => isCoverageUnit(entryUnit));
    const areaText = areaEntries.length > 0
        ? areaEntries.map(([entryUnit, qty]) => `${safeNumber(qty)} ${entryUnit}`).join(', ')
        : (parseFloat(totalReservedCoverageArea) > 0 ? `${safeNumber(totalReservedCoverageArea)} ${coverageAreaUnit}` : '');
    const primaryEntries = quantityEntries.length > 0
        ? quantityEntries
        : [[unit, totalReservedQuantity]];

    return primaryEntries.map(([entryUnit, qty], index) => (
        `${safeNumber(qty)} ${entryUnit}${index === 0 && areaText ? ` (${areaText})` : ''}`
    ));
};

const formatLineReservedQuantity = (detail) => {
    const areaText = parseFloat(detail?.reservedCoverageArea) > 0
        ? ` (${safeNumber(detail.reservedCoverageArea)} ${detail.coverageAreaUnit || 'sq.ft'})`
        : '';

    return `${safeNumber(detail?.reservedQuantity)} ${detail?.unit || 'pcs'}${areaText}`;
};

// Helper function to format IDs (from TallyQuotation.js)
const formatId = (id) => {
  if (id === null || id === undefined) return '';
  
  // If it's already a string, return it
  if (typeof id === 'string') return id;
  
  // Handle different object formats
  if (typeof id === 'object') {
    // For MongoDB ObjectId representation
    if (id.number !== undefined) return String(id.number);
    if (id._id !== undefined) return String(id._id);
    if (id.id !== undefined) return String(id.id);
    
    // For numeric values that might be objects
    if (id.toString && typeof id.toString === 'function') {
      return id.toString();
    }
    
    // Last resort - try to stringify
    try {
      return JSON.stringify(id);
    } catch (e) {
      return '[Object]';
    }
  }
  
  // For any other type, convert to string
  return String(id);
};

function Dashboard({ dashboardAccess = [] }) {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [loading, setLoading] = useState(true);
    
    // Get user's role-based access
    const userRoleAccess = user?.user?.role?.dashboardAccess || [];

    // A module is available only when BOTH the domain and the role grant it.
    const {
        hasDeliveryOrderAccess,
        hasQuotationNewAccess,
        hasTallyQuotationAccess,
        hasTallyItemsAccess,
        hasTallyLedgerAccess,
        hasVisitorAccess,
    } = moduleAccessFlags(dashboardAccess, userRoleAccess);
    const [visitorStats, setVisitorStats] = useState(null);
    const [visitorStatsLoading, setVisitorStatsLoading] = useState(false);
    const [personalStats, setPersonalStats] = useState(null);
    const [personalStatsLoading, setPersonalStatsLoading] = useState(false);
    const [personalPeriod, setPersonalPeriod] = useState('month');
    const [dashboardPrefs, setDashboardPrefs] = useState(null);
    const [recentTab, setRecentTab] = useState('delivery');
    const [visitorDistTab, setVisitorDistTab] = useState('creator');
    const [visitorShowAllTeam, setVisitorShowAllTeam] = useState(false);
    const [personRole, setPersonRole] = useState('createdBy');
    const [personSearch, setPersonSearch] = useState('');
    const [personModule, setPersonModule] = useState(null);

    useEffect(() => {
        if (!user?.user?._id) return undefined;
        const companyId = user?.user?.company?._id || user?.user?.company;
        if (!companyId) return undefined;
        let active = true;
        setPersonalStatsLoading(true);
        setVisitorStatsLoading(true);
        api
            .get(`${process.env.REACT_APP_API_BASE_URL}/dashboard/personal-stats`, {
                params: { companyId, userId: user.user._id, period: personalPeriod },
            })
            .then(res => {
                if (!active) return;
                const data = res.data?.data || null;
                setPersonalStats(data);
                setVisitorStats(data?.visitor || null);
            })
            .catch(() => {})
            .finally(() => {
                if (active) {
                    setPersonalStatsLoading(false);
                    setVisitorStatsLoading(false);
                }
            });
        return () => {
            active = false;
        };
    }, [user, personalPeriod]);

    // Per-user dashboard section preferences (managed from Profile).
    useEffect(() => {
        const uid = user?.user?._id;
        if (!uid) return undefined;
        let active = true;
        api.get(`${process.env.REACT_APP_API_BASE_URL}/employees/${uid}`)
            .then(res => { if (active) setDashboardPrefs(res.data?.dashboardPreferences || { hidden: [] }); })
            .catch(() => {});
        return () => { active = false; };
    }, [user]);
    
    // Debug logging
    console.log('Dashboard - Domain Access:', dashboardAccess);
    console.log('Dashboard - Role Access:', userRoleAccess);
    console.log('Dashboard - hasDeliveryOrderAccess (both checks):', hasDeliveryOrderAccess);
    console.log('Dashboard - hasQuotationNewAccess (both checks):', hasQuotationNewAccess);
    console.log('Dashboard - hasTallyQuotationAccess (both checks):', hasTallyQuotationAccess);
    console.log('Dashboard - hasTallyItemsAccess (both checks):', hasTallyItemsAccess);
    console.log('Dashboard - hasTallyLedgerAccess (both checks):', hasTallyLedgerAccess);
    
    // Delivery Orders State
    const [deliveryOrders, setDeliveryOrders] = useState([]);
    const [deliveryOrderStats, setDeliveryOrderStats] = useState({
        total: 0,
        pending: 0,
        completed: 0,
        cancelled: 0,
        totalAmount: 0,
        weeklyData: [],
        aging: { d0_7: 0, d8_30: 0, d31plus: 0 },
        topItems: []
    });
    const [deliveryOrdersChartData, setDeliveryOrdersChartData] = useState({ createdBy: [], salesman: [] }); // For pie charts

    // Quotation New State
    const [quotations, setQuotations] = useState([]);
    const [quotationStats, setQuotationStats] = useState({
        total: 0,
        pending: 0,
        completed: 0,
        cancelled: 0,
        partialComplete: 0,
        refFollow: 0,
        totalValue: 0,
        weeklyData: []
    });
    const [quotationsChartData, setQuotationsChartData] = useState({ createdBy: [], salesman: [] }); // For pie charts

    // Tally Quotation State
    const [tallyQuotations, setTallyQuotations] = useState([]);
    const [tallyQuotationStats, setTallyQuotationStats] = useState({
        total: 0,
        pending: 0,
        completed: 0,
        partialComplete: 0,
        refFollow: 0,
        cancelled: 0,
        totalValue: 0,
        weeklyData: []
    });
    const [tallyQuotationsChartData, setTallyQuotationsChartData] = useState({ createdBy: [], salesman: [] }); // For pie charts

    // Tally Items State
    const [tallyItemsStats, setTallyItemsStats] = useState({
        total: 0,
        withBrands: 0,
        withParameters: 0,
        withImages: 0,
        last7Days: []
    });

    // Tally Item Usage Report State
    const [reportSearchTerm, setReportSearchTerm] = useState('');
    const [reportSearchResults, setReportSearchResults] = useState([]);
    const [selectedReportItem, setSelectedReportItem] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);
    const [reportSearchLoading, setReportSearchLoading] = useState(false);
    const [reportActiveTab, setReportActiveTab] = useState('pi'); // 'pi', 'tq', 'do'

    // Get first day and last day dates for current month default
    const getFirstDayOfCurrentMonth = () => {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        return `${y}-${m}-01`;
    };

    const getTodayDateString = () => {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const [reportDateRange, setReportDateRange] = useState({
        startDate: getFirstDayOfCurrentMonth(),
        endDate: getTodayDateString()
    });

    // Reserved Tally Items Report State
    const [reservedItemsData, setReservedItemsData] = useState(null);
    const [reservedItemsLoading, setReservedItemsLoading] = useState(false);
    const [selectedReservedItem, setSelectedReservedItem] = useState(null);
    const [reservedItemDetails, setReservedItemDetails] = useState(null);
    const [reservedItemDetailsLoading, setReservedItemDetailsLoading] = useState(false);
    const [reservedSearch, setReservedSearch] = useState('');
    const [reservedPage, setReservedPage] = useState(1);
    const [reservedPageSize, setReservedPageSize] = useState(10);

    const fetchReservedItems = async () => {
        setReservedItemsLoading(true);
        try {
            const companyId = user?.user?.company?._id || user?.user?.company;
            const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/reports/reserved-tally-items`, {
                params: companyId ? { companyId } : {}
            });
            if (response.data && response.data.success) {
                setReservedItemsData(response.data);
            } else {
                console.error('Failed to load reserved items:', response.data?.error);
            }
        } catch (error) {
            console.error('Error fetching reserved tally items:', error);
        } finally {
            setReservedItemsLoading(false);
        }
    };

    const fetchReservedItemDetails = async (itemId) => {
        setReservedItemDetailsLoading(true);
        try {
            const companyId = user?.user?.company?._id || user?.user?.company;
            const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/reports/reserved-tally-items/${itemId}/details`, {
                params: companyId ? { companyId } : {}
            });
            if (response.data && response.data.success) {
                setReservedItemDetails(response.data);
            } else {
                console.error('Failed to load reserved item details:', response.data?.error);
            }
        } catch (error) {
            console.error('Error fetching reserved item details:', error);
        } finally {
            setReservedItemDetailsLoading(false);
        }
    };

    const handleSelectReservedItem = (item) => {
        setSelectedReservedItem(item);
        setShowReservedItemModal(true);
        fetchReservedItemDetails(item.itemId);
    };

    const closeReservedItemModal = () => {
        setShowReservedItemModal(false);
        setSelectedReservedItem(null);
        setReservedItemDetails(null);
    };

    const handleReviewPDFReport = () => {
        if (!selectedReportItem) return;
        const reviewUrl = `/tally-item-usage-report/${selectedReportItem._id}?startDate=${reportDateRange.startDate}&endDate=${reportDateRange.endDate}`;
        window.open(reviewUrl, '_blank');
    };

    const handleSelectReportItem = async (item) => {
        setSelectedReportItem(item);
        setReportSearchTerm(item.Name || '');
        setReportSearchResults([]);
        setReportLoading(true);
        setReportData(null);
        try {
            const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/tally/items/${item._id}/usage-report`, {
                params: {
                    startDate: reportDateRange.startDate,
                    endDate: reportDateRange.endDate
                }
            });
            if (response.data && response.data.success) {
                setReportData(response.data.data);
            } else {
                console.error('Failed to load item report:', response.data?.error);
            }
        } catch (error) {
            console.error('Error fetching tally item report detail:', error);
        } finally {
            setReportLoading(false);
        }
    };

    // Refetch report details if dates change when an item remains selected
    useEffect(() => {
        if (selectedReportItem) {
            handleSelectReportItem(selectedReportItem);
        }
    }, [reportDateRange.startDate, reportDateRange.endDate]);

    // Load reserved tally items report once on mount
    useEffect(() => {
        fetchReservedItems();
    }, []);

    // Handle Tally Item Report search
    useEffect(() => {
        if (!reportSearchTerm || reportSearchTerm.trim() === '') {
            setReportSearchResults([]);
            return;
        }

        // If the search matches the currently selected item label, don't trigger a new search
        if (selectedReportItem && reportSearchTerm === (selectedReportItem.Name || '')) {
            return;
        }

        const fetchResults = async () => {
            setReportSearchLoading(true);
            try {
                const companyId = user?.user?.company?._id || user?.user?.company;
                const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/tally/items`, {
                    params: {
                        company: companyId,
                        search: reportSearchTerm,
                        limit: 10
                    }
                });
                if (response.data && response.data.data) {
                    setReportSearchResults(response.data.data);
                } else if (Array.isArray(response.data)) {
                    setReportSearchResults(response.data);
                }
            } catch (error) {
                console.error('Error searching tally items for report:', error);
            } finally {
                setReportSearchLoading(false);
            }
        };

        const debounceTimer = setTimeout(fetchResults, 400);
        return () => clearTimeout(debounceTimer);
    }, [reportSearchTerm, user, selectedReportItem]);

    // Tally Ledger State
    const [tallyLedgerStats, setTallyLedgerStats] = useState({
        total: 0,
        last7Days: []
    });

    // Pagination State
    const [deliveryOrderPage, setDeliveryOrderPage] = useState(1);
    const [quotationPage, setQuotationPage] = useState(1);
    const [tallyQuotationPage, setTallyQuotationPage] = useState(1);
    const itemsPerPage = 10;

    // Total counts for pagination
    const [deliveryOrderTotal, setDeliveryOrderTotal] = useState(0);
    const [quotationTotal, setQuotationTotal] = useState(0);
    const [tallyQuotationTotal, setTallyQuotationTotal] = useState(0);

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [showReservedItemModal, setShowReservedItemModal] = useState(false);

    // Search and Filter State (temp values for input)
    const [deliveryOrderSearchTemp, setDeliveryOrderSearchTemp] = useState('');
    const [quotationSearchTemp, setQuotationSearchTemp] = useState('');
    const [tallyQuotationSearchTemp, setTallyQuotationSearchTemp] = useState('');
    const [quotationDateRangeTemp, setQuotationDateRangeTemp] = useState({ startDate: '', endDate: '' });
    const [tallyQuotationDateRangeTemp, setTallyQuotationDateRangeTemp] = useState({ startDate: '', endDate: '' });
    
    // Applied filters (actual values used in API calls)
    const [deliveryOrderSearch, setDeliveryOrderSearch] = useState('');
    const [quotationSearch, setQuotationSearch] = useState('');
    const [tallyQuotationSearch, setTallyQuotationSearch] = useState('');
    const [quotationDateRange, setQuotationDateRange] = useState({ startDate: '', endDate: '' });
    const [tallyQuotationDateRange, setTallyQuotationDateRange] = useState({ startDate: '', endDate: '' });

    // Check dark mode
    useEffect(() => {
        const checkDarkMode = () => {
            const darkMode = localStorage.getItem('darkMode') === 'true';
            setIsDarkMode(darkMode);
        };
        
        checkDarkMode();
        const interval = setInterval(checkDarkMode, 500);
        return () => clearInterval(interval);
    }, []);

    // Fetch all data
    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            try {
                const companyId = user.user.company._id || user.user.company;
                console.log('Dashboard fetchAllData - companyId:', companyId);
                
                if (!companyId) {
                    console.error('Company ID is missing from user object');
                    setLoading(false);
                    return;
                }
                
                // Fetch data only for modules with access
                const fetchPromises = [];
                
                if (hasDeliveryOrderAccess) {
                    fetchPromises.push(fetchDeliveryOrders(companyId, deliveryOrderPage));
                }
                if (hasQuotationNewAccess) {
                    fetchPromises.push(fetchQuotations(companyId, quotationPage));
                }
                if (hasTallyQuotationAccess) {
                    fetchPromises.push(fetchTallyQuotations(companyId, tallyQuotationPage));
                }
                if (hasTallyItemsAccess) {
                    fetchPromises.push(fetchTallyItemsStats(companyId));
                }
                if (hasTallyLedgerAccess) {
                    fetchPromises.push(fetchTallyLedgerStats(companyId));
                }
                
                await Promise.all(fetchPromises);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchAllData();
    }, [user, hasDeliveryOrderAccess, hasQuotationNewAccess, hasTallyQuotationAccess, hasTallyItemsAccess, hasTallyLedgerAccess]);

    // Refetch delivery orders when page or applied search changes
    useEffect(() => {
        if (!loading && user && hasDeliveryOrderAccess) {
            const companyId = user.user.company._id || user.user.company;
            fetchDeliveryOrders(companyId, deliveryOrderPage);
        }
    }, [deliveryOrderPage, deliveryOrderSearch]);

    // Refetch quotations when page, applied search, or applied date range changes
    useEffect(() => {
        if (!loading && user && hasQuotationNewAccess) {
            const companyId = user.user.company._id || user.user.company;
            fetchQuotations(companyId, quotationPage);
        }
    }, [quotationPage, quotationSearch, quotationDateRange]);

    // Refetch tally quotations when page, applied search, or applied date range changes
    useEffect(() => {
        if (!loading && user && hasTallyQuotationAccess) {
            const companyId = user.user.company._id || user.user.company;
            fetchTallyQuotations(companyId, tallyQuotationPage);
        }
    }, [tallyQuotationPage, tallyQuotationSearch, tallyQuotationDateRange]);

    // Debounced search (temp -> applied) for the recent-records tabs.
    // Reset to page 1 so a search always starts from the first page of results.
    useEffect(() => {
        const t = setTimeout(() => {
            setDeliveryOrderSearch(deliveryOrderSearchTemp.trim());
            setDeliveryOrderPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [deliveryOrderSearchTemp]);
    useEffect(() => {
        const t = setTimeout(() => {
            setQuotationSearch(quotationSearchTemp.trim());
            setQuotationPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [quotationSearchTemp]);
    useEffect(() => {
        const t = setTimeout(() => {
            setTallyQuotationSearch(tallyQuotationSearchTemp.trim());
            setTallyQuotationPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [tallyQuotationSearchTemp]);

    const fetchDeliveryOrders = async (companyId, page = 1) => {
        try {
            // Build query params
            let queryParams = `page=${page}&limit=${itemsPerPage}&companyId=${companyId}`;
            if (deliveryOrderSearch) {
                queryParams += `&search=${encodeURIComponent(deliveryOrderSearch)}`;
            }
            
            const response = await api.get(
                `${process.env.REACT_APP_API_BASE_URL}/delivery-orders/dashboard-recent?${queryParams}`
            );
            const result = response.data;
            setDeliveryOrders(result.orders || []);
            setDeliveryOrderTotal(result.count || 0);
            
            // Calculate stats
            const statsResponse = await api.get(
                `${process.env.REACT_APP_API_BASE_URL}/delivery-orders/dashboard-stats?companyId=${companyId}`
            );
            setDeliveryOrderStats(statsResponse.data || {
                total: result.count || 0,
                pending: 0,
                completed: 0,
                cancelled: 0,
                totalAmount: 0,
                weeklyData: []
            });

            // Fetch pie chart data from backend
            const [createdByResponse, salesmanResponse] = await Promise.all([
                api.get(`${process.env.REACT_APP_API_BASE_URL}/delivery-orders/dashboard-created-by?companyId=${companyId}`),
                api.get(`${process.env.REACT_APP_API_BASE_URL}/delivery-orders/dashboard-salesman?companyId=${companyId}`)
            ]);
            
            setDeliveryOrdersChartData({
                createdBy: createdByResponse.data || [],
                salesman: salesmanResponse.data || []
            });
        } catch (error) {
            console.error('Error fetching delivery orders:', error);
        }
    };

    const fetchQuotations = async (companyId, page = 1) => {
        try {
            // Build query params
            let queryParams = `page=${page}&limit=${itemsPerPage}&companyId=${companyId}&summary=true`;
            if (quotationSearch) {
                queryParams += `&search=${encodeURIComponent(quotationSearch)}`;
            }
            if (quotationDateRange.startDate) {
                queryParams += `&startDate=${quotationDateRange.startDate}`;
            }
            if (quotationDateRange.endDate) {
                queryParams += `&endDate=${quotationDateRange.endDate}`;
            }

            // Run list and stats in parallel; stats is independent so it always succeeds
            const [listResult, statsResult, createdByResult, salesmanResult] = await Promise.allSettled([
                api.get(`${process.env.REACT_APP_API_BASE_URL}/quotation-new/all?${queryParams}`),
                api.get(`${process.env.REACT_APP_API_BASE_URL}/quotation-new/stats?companyId=${companyId}`),
                api.get(`${process.env.REACT_APP_API_BASE_URL}/quotation-new/dashboard-created-by?companyId=${companyId}`),
                api.get(`${process.env.REACT_APP_API_BASE_URL}/quotation-new/dashboard-salesman?companyId=${companyId}`)
            ]);

            if (listResult.status === 'fulfilled') {
                const result = listResult.value.data;
                setQuotations(result.quotations || []);
                setQuotationTotal(result.count || 0);
            } else {
                console.error('Error fetching quotation list:', listResult.reason);
            }

            if (statsResult.status === 'fulfilled') {
                setQuotationStats(statsResult.value.data || {
                    total: 0, pending: 0, completed: 0, cancelled: 0,
                    partialComplete: 0, refFollow: 0, totalValue: 0, weeklyData: []
                });
            } else {
                console.error('Error fetching quotation stats:', statsResult.reason);
            }

            setQuotationsChartData({
                createdBy: createdByResult.status === 'fulfilled' ? (createdByResult.value.data || []) : [],
                salesman: salesmanResult.status === 'fulfilled' ? (salesmanResult.value.data || []) : []
            });
        } catch (error) {
            console.error('Error fetching quotations:', error);
        }
    };

    const fetchTallyQuotations = async (companyId, page = 1) => {
        try {
            // Build query params
            let queryParams = `page=${page}&limit=${itemsPerPage}&companyId=${companyId}`;
            if (tallyQuotationSearch) {
                queryParams += `&search=${encodeURIComponent(tallyQuotationSearch)}`;
            }
            if (tallyQuotationDateRange.startDate) {
                queryParams += `&startDate=${tallyQuotationDateRange.startDate}`;
            }
            if (tallyQuotationDateRange.endDate) {
                queryParams += `&endDate=${tallyQuotationDateRange.endDate}`;
            }
            
            const response = await api.get(
                `${process.env.REACT_APP_API_BASE_URL}/tally-quotation/all?${queryParams}`
            );
            const result = response.data;
            setTallyQuotations(result.quotations || []);
            setTallyQuotationTotal(result.count || 0);
            
            // Calculate stats
            const statsResponse = await api.get(
                `${process.env.REACT_APP_API_BASE_URL}/tally-quotation/stats?companyId=${companyId}`
            );
            setTallyQuotationStats(statsResponse.data || {
                total: result.count || 0,
                pending: 0,
                completed: 0,
                partialComplete: 0,
                refFollow: 0,
                cancelled: 0,
                totalValue: 0,
                weeklyData: []
            });

            // Fetch pie chart data from backend
            const [createdByResponse, salesmanResponse] = await Promise.all([
                api.get(`${process.env.REACT_APP_API_BASE_URL}/tally-quotation/dashboard-created-by?companyId=${companyId}`),
                api.get(`${process.env.REACT_APP_API_BASE_URL}/tally-quotation/dashboard-salesman?companyId=${companyId}`)
            ]);
            
            setTallyQuotationsChartData({
                createdBy: createdByResponse.data || [],
                salesman: salesmanResponse.data || []
            });
        } catch (error) {
            console.error('Error fetching tally quotations:', error);
        }
    };

    const fetchTallyItemsStats = async (companyId) => {
        try {
            if (!companyId) {
                console.error('Company ID is undefined in fetchTallyItemsStats');
                return;
            }
            const response = await api.get(
                `${process.env.REACT_APP_API_BASE_URL}/tally/tally-items/dashboard-stats?companyId=${companyId}`
            );
            setTallyItemsStats(response.data || {
                total: 0,
                withBrands: 0,
                withParameters: 0,
                withImages: 0,
                last7Days: []
            });
        } catch (error) {
            console.error('Error fetching tally items stats:', error);
        }
    };

    const fetchTallyLedgerStats = async (companyId) => {
        try {
            if (!companyId) {
                console.error('Company ID is undefined in fetchTallyLedgerStats');
                return;
            }
            const response = await api.get(
                `${process.env.REACT_APP_API_BASE_URL}/tally-ledgers/dashboard-stats?companyId=${companyId}`
            );
            setTallyLedgerStats(response.data || {
                total: 0,
                last7Days: []
            });
        } catch (error) {
            console.error('Error fetching tally ledger stats:', error);
        }
    };

    // Apply filter handlers
    const handleApplyQuotationFilters = () => {
        setQuotationSearch(quotationSearchTemp.trim());
        setQuotationDateRange(quotationDateRangeTemp);
        setQuotationPage(1);
    };

    const handleApplyTallyQuotationFilters = () => {
        setTallyQuotationSearch(tallyQuotationSearchTemp.trim());
        setTallyQuotationDateRange(tallyQuotationDateRangeTemp);
        setTallyQuotationPage(1);
    };

    const handleClearQuotationFilters = () => {
        setQuotationSearchTemp('');
        setQuotationDateRangeTemp({ startDate: '', endDate: '' });
        setQuotationSearch('');
        setQuotationDateRange({ startDate: '', endDate: '' });
        setQuotationPage(1);
    };

    const handleClearTallyQuotationFilters = () => {
        setTallyQuotationSearchTemp('');
        setTallyQuotationDateRangeTemp({ startDate: '', endDate: '' });
        setTallyQuotationSearch('');
        setTallyQuotationDateRange({ startDate: '', endDate: '' });
        setTallyQuotationPage(1);
    };

    // View record details
    const handleViewRecord = async (record, type) => {
        setShowModal(true);

        try {
            // Fetch full record with populated fields
            let fullRecord = record;
            if (type === 'delivery') {
                try {
                    const response = await api.get(
                        `${process.env.REACT_APP_API_BASE_URL}/delivery-orders/details/${record._id}`
                    );
                    fullRecord = response.data.deliveryOrder || response.data || record;
                    
                    // Transform items to products array for consistent structure
                    if (fullRecord.items && fullRecord.items.length > 0) {
                        fullRecord.products = fullRecord.items.map(item => ({
                            item: item.itemId, // The populated itemId becomes item
                            Name: item.Name,
                            name: item.Name || item.itemId?.Name || item.itemId?.name,
                            description: item.description || '-',
                            itemDescription: item.description,
                            hsn: item.itemId?.hsnCode || '-',
                            hsnCode: item.itemId?.hsnCode,
                            quantity: item.quantity || 0,
                            units: item.units || 'box',
                            unit: item.units,
                            rate: item.rate || 0,
                            unitPrice: item.rate,
                            price: item.rate,
                            discount: item.discount || 0,
                            printType: item.printType,
                            amountBeforeDiscount: item.amountBeforeDiscount || 0,
                            amountAfterDiscount: item.amountAfterDiscount || 0,
                            totalPrice: item.amountAfterDiscount,
                            amount: item.amountAfterDiscount,
                            tt: item.tt
                        }));
                    }
                } catch (err) {
                    console.error('Error fetching full delivery order:', err);
                    fullRecord = record; // Fall back to existing record
                }
            } else if (type === 'quotation') {
                try {
                    const response = await api.get(
                        `${process.env.REACT_APP_API_BASE_URL}/quotation-new/data/${record._id}`
                    );
                    fullRecord = response.data || record;
                    
                    // Transform sections into products array for Items Table
                    if (fullRecord.sections && fullRecord.sections.length > 0) {
                        fullRecord.products = fullRecord.sections.flatMap(section => 
                            section.items
                                .filter(item => item && (item.item || item.customName)) // Skip items without valid data
                                .map(item => ({
                                    name: item.customName || item.item?.skuDescription || item.item?.skuCode || 'N/A',
                                    productName: item.customName || item.item?.skuDescription,
                                    itemName: item.item?.skuDescription || item.item?.skuCode,
                                    description: item.narration || item.applicationArea || '-',
                                    itemDescription: item.narration,
                                    hsn: item.item?.hsnCode || '-',
                                    hsnCode: item.item?.hsnCode,
                                    quantity: item.quantity || 0,
                                    unit: item.unit || 'pcs',
                                    listedPrice: item.listedPrice || 0, // Original price before discount
                                    unitPrice: item.offerPrice || item.listedPrice || 0,
                                    price: item.offerPrice,
                                    rate: item.offerPrice, // Final price after discount
                                    discount: item.discount || 0,
                                    ttAmount: item.discount > 0 ? (item.quantity * item.listedPrice * item.discount / 100) : 0,
                                    totalPrice: item.offerPriceTotal || (item.quantity * item.offerPrice) || 0,
                                    amount: item.offerPriceTotal,
                                    printType: null, // QuotationNew doesn't have printType
                                    offerType: item.offerType,
                                    applicationArea: item.applicationArea,
                                    coverageArea: item.coverageArea,
                                    coverageAreaUnit: item.coverageAreaUnit,
                                    boxes: item.boxes,
                                    pieces: item.pieces,
                                    skuCode: item.item?.skuCode,
                                    brand: item.item?.brand,
                                    category: item.item?.category
                                }))
                        );
                    }
                } catch (err) {
                    console.error('Error fetching full quotation:', err);
                    fullRecord = record; // Fall back to existing record
                }
            } else if (type === 'tally') {
                try {
                    const response = await api.get(
                        `${process.env.REACT_APP_API_BASE_URL}/tally-quotation/${record._id}`
                    );
                    fullRecord = response.data || record;
                } catch (err) {
                    console.error('Error fetching full tally quotation:', err);
                    fullRecord = record; // Fall back to existing record
                }
            }

            setSelectedRecord({ ...fullRecord, type });
        } catch (error) {
            console.error('Error fetching record details:', error);
            // Still set the record even if fetch fails
            setSelectedRecord({ ...record, type });
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedRecord(null);
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    // Pagination helper functions
    const getTotalPages = (totalItems) => {
        return Math.ceil(totalItems / itemsPerPage);
    };

    // Pagination Component with improved handling for large page counts
    const PaginationControls = ({ currentPage, totalItems, onPageChange }) => {
        const totalPages = getTotalPages(totalItems);
        
        if (totalPages <= 1) return null;

        // Generate page numbers to display
        const getPageNumbers = () => {
            const pages = [];
            const maxPagesToShow = 7; // Show max 7 page buttons
            
            if (totalPages <= maxPagesToShow) {
                // Show all pages if total is small
                for (let i = 1; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                // Always show first page
                pages.push(1);
                
                let startPage = Math.max(2, currentPage - 2);
                let endPage = Math.min(totalPages - 1, currentPage + 2);
                
                // Adjust if we're near the beginning
                if (currentPage <= 3) {
                    endPage = 5;
                }
                
                // Adjust if we're near the end
                if (currentPage >= totalPages - 2) {
                    startPage = totalPages - 4;
                }
                
                // Add ellipsis after first page if needed
                if (startPage > 2) {
                    pages.push('...');
                }
                
                // Add middle pages
                for (let i = startPage; i <= endPage; i++) {
                    pages.push(i);
                }
                
                // Add ellipsis before last page if needed
                if (endPage < totalPages - 1) {
                    pages.push('...');
                }
                
                // Always show last page
                pages.push(totalPages);
            }
            
            return pages;
        };

        const pageNumbers = getPageNumbers();

        return (
            <div className={`px-4 sm:px-6 py-4 border-t ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
                {/* Mobile Layout */}
                <div className="flex flex-col gap-3 sm:hidden">
                    <div className={`text-xs text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} results
                    </div>
                    <div className="flex items-center justify-between gap-2">
                        <button
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                currentPage === 1
                                    ? isDarkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                            }`}
                        >
                            ‹ Previous
                        </button>
                        <div className={`px-3 py-2 rounded-md text-sm font-medium ${isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'}`}>
                            {currentPage} / {totalPages}
                        </div>
                        <button
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                currentPage === totalPages
                                    ? isDarkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                            }`}
                        >
                            Next ›
                        </button>
                    </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden sm:flex items-center justify-between">
                    <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`}>
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} results
                    </div>
                    <div className="flex gap-1 items-center">
                        <button
                            onClick={() => onPageChange(1)}
                            disabled={currentPage === 1}
                            className={`hidden md:block px-2 py-1 rounded-md text-sm font-medium transition-colors ${
                                currentPage === 1
                                    ? isDarkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                            }`}
                            title="First page"
                        >
                            ««
                        </button>
                        <button
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                currentPage === 1
                                    ? isDarkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                            }`}
                        >
                            <span className="hidden md:inline">Previous</span>
                            <span className="md:hidden">‹</span>
                        </button>
                        {pageNumbers.map((page, index) => (
                            page === '...' ? (
                                <span key={`ellipsis-${index}`} className={`px-2 py-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                    ...
                                </span>
                            ) : (
                                <button
                                    key={page}
                                    onClick={() => onPageChange(page)}
                                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                        currentPage === page
                                            ? isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                                            : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                                    }`}
                                >
                                    {page}
                                </button>
                            )
                        ))}
                        <button
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                currentPage === totalPages
                                    ? isDarkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                            }`}
                        >
                            <span className="hidden md:inline">Next</span>
                            <span className="md:hidden">›</span>
                        </button>
                        <button
                            onClick={() => onPageChange(totalPages)}
                            disabled={currentPage === totalPages}
                            className={`hidden md:block px-2 py-1 rounded-md text-sm font-medium transition-colors ${
                                currentPage === totalPages
                                    ? isDarkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                            }`}
                            title="Last page"
                        >
                            »»
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                    <p className={`mt-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading Dashboard...</p>
                </div>
            </div>
        );
    }

    const renderPersonalModuleCard = (title, moduleStats, moduleKey) => {
        if (!moduleStats) return null;
        const me = moduleStats.me || {};
        const team = moduleStats.team || {};
        const company = moduleStats.company || {};
        const statusSet = PERSONAL_STATUS_SETS[moduleKey] || [];
        const share = (value, total) => {
            const v = Number(value) || 0;
            const t = Number(total) || 0;
            return t > 0 ? Math.min(100, Math.round((v / t) * 100)) : 0;
        };
        const bar = (value, total, colorClass) => (
            <div className={`mt-1 h-1.5 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <div className={`h-1.5 rounded-full ${colorClass}`} style={{ width: `${share(value, total)}%` }} />
            </div>
        );
        const metricLabel = `text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`;
        const metricValue = `text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`;
        return (
            <div key={title} className={`rounded-xl shadow-lg border p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center justify-between mb-3">
                    <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
                    {team.myRank > 0 && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${isDarkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                            Rank #{team.myRank}
                        </span>
                    )}
                </div>
                <div className="space-y-3 mb-3">
                    <div>
                        <div className="flex items-baseline justify-between">
                            <span className={metricLabel}>Created by me</span>
                            <span className={metricLabel}>of {company.count ?? 0} (total issued)</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={metricValue}>{me.created ?? 0}</span>
                            <span className={metricLabel}>({share(me.created, company.count)}%)</span>
                        </div>
                        {bar(me.created, company.count, 'bg-indigo-500')}
                    </div>
                    <div>
                        <div className="flex items-baseline justify-between">
                            <span className={metricLabel}>As salesperson</span>
                            <span className={metricLabel}>of {company.salesRecords ?? 0} (total issued)</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={metricValue}>{me.asSalesman ?? 0}</span>
                            <span className={metricLabel}>({share(me.asSalesman, company.salesRecords)}%)</span>
                        </div>
                        {bar(me.asSalesman, company.salesRecords, 'bg-sky-500')}
                    </div>
                    <div>
                        <span className={metricLabel}>My value</span>
                        <div className="flex items-center gap-2">
                            <span className={`text-xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                ₹{formatNumberWithCommas(me.amount || 0)}
                            </span>
                            <span className={metricLabel}>({share(me.amount, company.amount)}%)</span>
                        </div>
                        {bar(me.amount, company.amount, 'bg-green-500')}
                    </div>
                </div>
                {statusSet.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {statusSet.map(s => (
                            <StatusPill key={s.key} label={s.label} value={me.status?.[s.key] ?? 0} colorClass={statusPillClass(s.label, isDarkMode)} isDark={isDarkMode} />
                        ))}
                    </div>
                )}
                {MODULE_BY_KEY[moduleKey] && (
                    <button
                        onClick={() => navigate(moduleRoute(moduleKey))}
                        className={`mt-3 w-full text-sm font-medium py-2 rounded-lg transition-colors ${isDarkMode ? 'bg-gray-700 text-indigo-300 hover:bg-gray-600' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`}
                    >
                        View {moduleLabel(moduleKey)} →
                    </button>
                )}
            </div>
        );
    };

    const renderPersonalRemindersCard = () => {
        const r = personalStats?.reminders;
        if (!r) return null;
        const counts = [
            { label: 'Pending', value: r.pending },
            { label: 'Overdue', value: r.overdue },
            { label: 'Due Today', value: r.dueToday },
            { label: 'Follow-ups', value: r.nextFollowUps },
        ];
        const statusEntries = Object.entries(r.status || {}).filter(([, n]) => n > 0);
        const typeEntries = Object.entries(r.byType || {})
            .filter(([, n]) => n > 0)
            .sort((a, b) => b[1] - a[1]);
        return (
            <div className={`rounded-xl shadow-lg border p-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center justify-between">
                    <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>My Reminders</h3>
                    <button onClick={() => navigate(moduleRoute('reminders'))} className="text-xs font-semibold text-indigo-500 hover:text-indigo-400">View all →</button>
                </div>
                <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {r.total ?? 0} reminders · your follow-ups at a glance
                </p>
                <div className="grid grid-cols-4 gap-2 mb-2">
                    {counts.map(c => (
                        <KpiCard key={c.label} label={c.label} value={c.value} isDark={isDarkMode} className="text-center" />
                    ))}
                </div>
                {statusEntries.length > 0 && (
                    <div className="mb-2">
                        <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Status</p>
                        <div className="flex flex-wrap gap-1.5">
                            {statusEntries.map(([s, n]) => (
                                <StatusPill key={s} label={s.replace(/-/g, ' ')} value={n} colorClass={statusPillClass(s, isDarkMode)} isDark={isDarkMode} />
                            ))}
                        </div>
                    </div>
                )}
                {typeEntries.length > 0 && (
                    <div className="mb-2">
                        <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Created type</p>
                        <div className="flex flex-wrap gap-1.5">
                            {typeEntries.map(([t, n]) => (
                                <span key={t} className={`text-xs px-2 py-0.5 rounded-full font-medium ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                                    {t || 'Other'}: {n}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
                {r.list?.length > 0 ? (
                    <div className={`max-h-48 overflow-y-auto text-xs divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-100'} ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {r.list.map(item => (
                            <div key={item.id} className="flex justify-between gap-2 py-1.5">
                                <span className="truncate">{item.message}</span>
                                <span className="whitespace-nowrap">
                                    {item.date ? new Date(item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : ''}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No upcoming reminders.</p>
                )}
            </div>
        );
    };

    const renderPersonalVisitorCard = () => {
        const me = personalStats?.visitor?.me;
        if (!me) return null;
        const counts = [
            { label: 'Visits attended', value: me.visitsAttended, sub: 'by me' },
            { label: 'Visitors added', value: me.visitorsAdded, sub: 'new records' },
            { label: 'Needs response', value: me.visitsNeedingResponse, sub: 'awaiting' },
        ];
        return (
            <div className={`rounded-xl shadow-lg border p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>My Visits</h3>
                <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Your visit activity for the selected period</p>
                <div className="grid grid-cols-2 gap-3">
                    {counts.map(c => (
                        <KpiCard key={c.label} label={c.label} value={c.value} sub={c.sub} isDark={isDarkMode} />
                    ))}
                </div>
                <button
                    onClick={() => navigate(moduleRoute('visitor'))}
                    className={`mt-3 w-full text-sm font-medium py-2 rounded-lg transition-colors ${isDarkMode ? 'bg-gray-700 text-indigo-300 hover:bg-gray-600' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`}
                >
                    View {moduleLabel('visitor')} →
                </button>
            </div>
        );
    };

    const prefs = dashboardPrefs || {};
    const hiddenSections = prefs.hidden || [];
    const layoutPrefs = prefs.layout || {};
    const uiPrefs = prefs.ui || {};
    const accent = uiPrefs.accent || '';
    const density = uiPrefs.density || 'comfortable';
    const orderedKeys = normalizeOrder(prefs.order);
    const orderIndex = (key) => {
        const index = orderedKeys.indexOf(key);
        return index === -1 ? 999 : index;
    };
    const showSection = (key) => !hiddenSections.includes(key);
    const sectionClass = (key) =>
        `dash-section ${layoutPrefs[key] === 'half' ? 'lg:col-span-1 col-span-1' : 'lg:col-span-2 col-span-1'}`;
    const sectionStyle = (key) => ({ order: orderIndex(key) });

    const chartPrefs = prefs.charts || {};
    const chartHiddenSections = chartPrefs.hidden || [];
    const chartLayoutPrefs = chartPrefs.layout || {};
    const chartOrderedKeys = normalizeChartOrder(chartPrefs.order);
    const chartGroupIndex = (key) => {
        const chart = DASHBOARD_CHARTS.find(c => c.key === key);
        if (!chart) return 999;
        const members = DASHBOARD_CHARTS.filter(c => c.group === chart.group).map(c => c.key);
        const ordered = chartOrderedKeys.filter(k => members.includes(k));
        const idx = ordered.indexOf(key);
        return idx === -1 ? 999 : idx;
    };
    const chartStyle = (key) => {
        const defaultWidth = DASHBOARD_CHARTS.find(c => c.key === key)?.defaultWidth || 'half';
        const width = chartLayoutPrefs[key] || defaultWidth;
        return {
            order: chartGroupIndex(key),
            gridColumn: width === 'full' ? '1 / -1' : undefined,
            display: chartHiddenSections.includes(key) ? 'none' : undefined,
        };
    };
    const showChart = (key) => !chartHiddenSections.includes(key);

    // Visitor Insights: compact KPI strip + tabbed distribution charts.
    const vs = visitorStats || {};
    const visitorKpiGroups = [
        { title: 'Company', items: [
            { label: 'Visits', value: vs.visitsInPeriod, color: 'text-indigo-500', sub: 'in period' },
            { label: 'New Visitors', value: vs.newVisitors, color: 'text-emerald-500', sub: 'first-time' },
            { label: 'Unique', value: vs.uniqueVisitors, color: 'text-sky-500', sub: 'distinct people' },
            { label: 'Returning', value: vs.returningVisitors, color: 'text-purple-500', sub: 'more than once' },
            { label: 'Pending Responses', value: vs.pendingResponses, color: 'text-rose-500' },
            { label: 'Quotations to Issue', value: vs.quotationsToIssue, color: 'text-orange-500' },
        ] },
        { title: 'My activity', items: [
            { label: 'Attended by Me', value: vs.attendedByMe, color: 'text-indigo-500' },
            { label: 'Responses by Me', value: vs.responsesUpdatedByMe, color: 'text-emerald-500' },
            { label: 'Needs Response', value: vs.me?.visitsNeedingResponse, color: 'text-amber-500' },
        ] },
    ];
    const visitorDistTabs = [
        showChart('visitor-creator') && { key: 'creator', label: 'By Creator', data: vs.byCreatedBy },
        showChart('visitor-salesperson') && { key: 'salesperson', label: 'By Salesperson', data: vs.bySalesperson },
    ].filter(Boolean);
    const activeDistTab = visitorDistTabs.some(t => t.key === visitorDistTab) ? visitorDistTab : (visitorDistTabs[0]?.key || 'creator');
    const activeDistData = visitorDistTabs.find(t => t.key === activeDistTab)?.data || [];

    // Person distribution combined across modules (with per-module status for the tooltip).
    const personEntries = (() => {
        const map = new Map();
        const add = (items, moduleKey) => {
            (items || []).forEach(item => {
                const name = item.name || 'Unassigned';
                if (!map.has(name)) map.set(name, { name, count: 0, modules: {} });
                const entry = map.get(name);
                entry.count += item.count || 0;
                entry.modules[moduleKey] = { count: item.count || 0, statusCounts: item.statusCounts || {} };
            });
        };
        if (hasDeliveryOrderAccess) add(deliveryOrdersChartData?.[personRole], 'delivery');
        if (hasQuotationNewAccess) add(quotationsChartData?.[personRole], 'quotation');
        if (hasTallyQuotationAccess) add(tallyQuotationsChartData?.[personRole], 'tally');
        return [...map.values()].sort((a, b) => b.count - a.count);
    })();
    const personSearchLower = personSearch.trim().toLowerCase();
    const personFiltered = personSearchLower
        ? personEntries.filter(p => p.name.toLowerCase().includes(personSearchLower))
        : personEntries;
    const personLookup = new Map(personFiltered.map(p => [p.name, p]));
    // Grow the chart with the number of people so every row (and its label) is visible.
    const personChartHeight = Math.min(Math.max(personFiltered.length * 30 + 40, 320), 1400);

    // Person Distribution: pick one module, show one bar per person stacked by
    // that module's statuses (clearer than grouped multi-module bars).
    const PERSON_MODULE_COLORS = {
        delivery: MODULE_COLORS.deliveryOrders,
        quotation: MODULE_COLORS.quotationNew,
        tally: MODULE_COLORS.tallyQuotation,
    };
    const PERSON_MODULES = [
        hasDeliveryOrderAccess && { key: 'delivery', label: 'Delivery Orders' },
        hasQuotationNewAccess && { key: 'quotation', label: 'Quotation New' },
        hasTallyQuotationAccess && { key: 'tally', label: 'Tally Quotations' },
    ].filter(Boolean);
    const activePersonModule = PERSON_MODULES.some(m => m.key === personModule)
        ? personModule
        : (PERSON_MODULES[0]?.key || 'delivery');
    const activePersonModuleLabel = PERSON_MODULES.find(m => m.key === activePersonModule)?.label || activePersonModule;

    const formatPersonTooltip = (params) => {
        const p = Array.isArray(params) ? params[0] : params;
        const entry = personLookup.get(p.name);
        const m = entry?.modules?.[activePersonModule];
        if (!entry || !m) return `${p.name}: ${p.value}`;
        const statuses = Object.entries(m.statusCounts || {})
            .filter(([, n]) => n > 0)
            .map(([s, n]) => `${s} ${n}`)
            .join(', ');
        return `<div style="font-weight:600;margin-bottom:2px">${entry.name} — ${m.count}</div>${activePersonModuleLabel}${statuses ? `: ${statuses}` : ''}`;
    };

    const personBarSeries = (() => {
        const mod = PERSON_MODULES.find(m => m.key === activePersonModule) || { key: activePersonModule, label: '' };
        const statuses = new Set();
        personFiltered.forEach(p => {
            Object.keys(p.modules[mod.key]?.statusCounts || {}).forEach(s => statuses.add(s));
        });
        if (statuses.size === 0) {
            return [{
                name: mod.label,
                data: personFiltered.map(p => p.modules[mod.key]?.count || 0),
                color: PERSON_MODULE_COLORS[mod.key],
            }];
        }
        return [...statuses].map(status => ({
            name: status,
            stack: 'status',
            color: statusHex(status) || PERSON_MODULE_COLORS[mod.key],
            data: personFiltered.map(p => p.modules[mod.key]?.statusCounts?.[status] || 0),
        }));
    })();

    // Tally item assignment combinations (brand / parameter / image).
    const ASSIGNMENT_COMBOS = [
        { b: 1, p: 1, i: 1, label: 'Brand + Parameter + Image' },
        { b: 1, p: 1, i: 0, label: 'Brand + Parameter' },
        { b: 1, p: 0, i: 1, label: 'Brand + Image' },
        { b: 0, p: 1, i: 1, label: 'Parameter + Image' },
        { b: 1, p: 0, i: 0, label: 'Brand only' },
        { b: 0, p: 1, i: 0, label: 'Parameter only' },
        { b: 0, p: 0, i: 1, label: 'Image only' },
        { b: 0, p: 0, i: 0, label: 'None' },
    ];
    const assignmentCombos = tallyItemsStats.combinations || [];
    const assignmentPieData = ASSIGNMENT_COMBOS
        .map(c => ({
            name: c.label,
            value: assignmentCombos
                .filter(x => (x.brand ? 1 : 0) === c.b && (x.parameter ? 1 : 0) === c.p && (x.image ? 1 : 0) === c.i)
                .reduce((sum, x) => sum + (x.count || 0), 0),
        }))
        .filter(d => d.value > 0);

    const recentTabs = [
        hasDeliveryOrderAccess && { key: 'delivery', label: 'Delivery Orders' },
        hasQuotationNewAccess && { key: 'quotation', label: 'Quotations' },
        hasTallyQuotationAccess && { key: 'tally', label: 'Tally Quotations' },
    ].filter(Boolean);
    const activeRecentTab = recentTabs.some(t => t.key === recentTab) ? recentTab : (recentTabs[0]?.key || 'delivery');

    // Reserved Tally Items: client-side search + pagination over the summary.
    const reservedSummary = reservedItemsData?.summary || [];
    const reservedSearchLower = reservedSearch.trim().toLowerCase();
    const reservedFiltered = reservedSearchLower
        ? reservedSummary.filter(item =>
            [item.itemName, item.brandName, item.partNo]
                .some(v => String(v || '').toLowerCase().includes(reservedSearchLower)))
        : reservedSummary;
    const reservedTotalPages = Math.max(1, Math.ceil(reservedFiltered.length / reservedPageSize));
    const reservedSafePage = Math.min(Math.max(reservedPage, 1), reservedTotalPages);
    const reservedPagedItems = reservedFiltered.slice(
        (reservedSafePage - 1) * reservedPageSize,
        reservedSafePage * reservedPageSize
    );

    return (
        <div
            className={`dash-root min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} p-6 ${density === 'compact' ? 'dash-compact' : ''}`}
            style={{ '--dash-accent': accent || '#6366F1' }}
        >
            <style>{densityCSS}</style>
            {/* Header */}
            <div className="mb-8">
                <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Dashboard Overview
                </h1>
                <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Welcome back, {user.user.name.split(' ')[0]}! Here's what's happening today.
                </p>
                <div className="mt-3 h-1 w-24 rounded-full" style={{ backgroundColor: 'var(--dash-accent)' }}></div>
            </div>

            {/* Sections grid (order + width driven by dashboard preferences) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Visitor Insights */}
            {hasVisitorAccess && showSection('visitor') && (
                <div className={sectionClass('visitor')} style={sectionStyle('visitor')}>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                        <div>
                            <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                Visitor Insights
                            </h2>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Company-wide visits, responses and follow-ups
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            {visitorStatsLoading && (
                                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Loading…
                                </span>
                            )}
                            <SegmentedTabs
                                size="sm"
                                isDark={isDarkMode}
                                value={personalPeriod}
                                onChange={setPersonalPeriod}
                                options={[{ key: '14d', label: '14 Days' }, { key: 'month', label: 'Month' }]}
                            />
                        </div>
                    </div>

                    {visitorStats && (
                        <>
                            {/* KPI strip (categorised) */}
                            {visitorKpiGroups.map((group) => (
                                <div key={group.title} className="mb-2">
                                    <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        {group.title}
                                    </p>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                                        {group.items.map((k) => (
                                            <KpiCard key={k.label} label={k.label} value={k.value} color={k.color} sub={k.sub} isDark={isDarkMode} />
                                        ))}
                                    </div>
                                </div>
                            ))}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {visitorDistTabs.length > 0 && (
                                <div
                                    style={{
                                        order: chartGroupIndex('visitor-creator'),
                                        gridColumn: chartLayoutPrefs['visitor-creator'] === 'full' ? '1 / -1' : undefined,
                                    }}
                                    className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow border p-4`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Visits by</h3>
                                        {visitorDistTabs.length > 1 && (
                                            <SegmentedTabs
                                                size="sm"
                                                isDark={isDarkMode}
                                                value={activeDistTab}
                                                onChange={setVisitorDistTab}
                                                options={visitorDistTabs.map(t => ({ key: t.key, label: t.label }))}
                                            />
                                        )}
                                    </div>
                                    {activeDistData.length > 0 ? (
                                        <LazyMount className="h-40">
                                            <EChart
                                                option={pieOption({
                                                    data: activeDistData.map(x => ({ name: x.name, value: x.count })),
                                                    isDark: isDarkMode,
                                                    donut: true,
                                                    centerLabel: 'Visits',
                                                })}
                                            />
                                        </LazyMount>
                                    ) : (
                                        <EmptyState message="No visits yet" isDark={isDarkMode} className="py-8" />
                                    )}
                                </div>
                                )}

                                <div style={chartStyle('visitor-daily')} className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow border p-4`}>
                                    <h3 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                        My Daily Visits
                                    </h3>
                                    {(visitorStats.me?.dailyTrend || []).length > 0 ? (
                                        <LazyMount className="h-40">
                                            <EChart
                                                option={lineOption({
                                                    labels: (visitorStats.me.dailyTrend || []).map(d =>
                                                        new Date(d.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                                                    ),
                                                    series: [
                                                        { name: 'Visits', data: (visitorStats.me.dailyTrend || []).map(d => d.visits), color: '#6366F1', fill: true },
                                                        { name: 'Positive', data: (visitorStats.me.dailyTrend || []).map(d => d.positive), color: '#22C55E', fill: true },
                                                    ],
                                                    isDark: isDarkMode,
                                                })}
                                            />
                                        </LazyMount>
                                    ) : (
                                        <EmptyState message="No visits in this period" isDark={isDarkMode} className="py-8" />
                                    )}
                                </div>
                            </div>

                            {/* Team leaderboard (compact) */}
                            <div className={`mt-3 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow border p-4`}>
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                        Team Leaderboard
                                        <span className={`ml-2 text-xs font-normal ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Your rank #{visitorStats.team?.myRank || 0} of {visitorStats.team?.totalSalespersons || 0}
                                        </span>
                                    </h3>
                                    {(visitorStats.team?.top || []).length > 3 && (
                                        <button onClick={() => setVisitorShowAllTeam(v => !v)} className="text-xs font-semibold text-indigo-500 hover:text-indigo-400">
                                            {visitorShowAllTeam ? 'Show top 3' : 'Show all'}
                                        </button>
                                    )}
                                </div>
                                <div className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                                    {(visitorShowAllTeam ? (visitorStats.team?.top || []) : (visitorStats.team?.top || []).slice(0, 3)).map((member, index) => {
                                        const isMe = String(member.id) === String(user.user._id);
                                        return (
                                            <div
                                                key={member.id || index}
                                                className={`flex items-center justify-between px-2 py-1.5 text-xs rounded ${isMe ? (isDarkMode ? 'bg-indigo-900/40' : 'bg-indigo-50') : ''}`}>
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className={`w-5 text-center font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{index + 1}</span>
                                                    <span className={`truncate font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{member.name}</span>
                                                    {isMe && <span className="text-[10px] font-bold text-indigo-500">YOU</span>}
                                                </div>
                                                <span className={`flex-shrink-0 font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{member.count}</span>
                                            </div>
                                        );
                                    })}
                                    {(visitorStats.team?.top || []).length === 0 && (
                                        <p className={`text-xs py-3 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No team activity</p>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}


            {/* Charts Section */}
            {showSection('charts') && (
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${sectionClass('charts')}`} style={sectionStyle('charts')}>
                {/* Delivery Orders Trend */}
                {hasDeliveryOrderAccess && (
                <>
                <TrendCard
                    title="Delivery Orders Trend (Last 7 Days)"
                    subtitle="Company-wide orders created per day"
                    labels={(deliveryOrderStats.weeklyData || []).map(d => d.label)}
                    data={(deliveryOrderStats.weeklyData || []).map(d => d.count)}
                    color={isDarkMode ? '#60A5FA' : '#3B82F6'}
                    isDark={isDarkMode}
                    style={chartStyle('do-trend')}
                />

                {/* Pending Aging */}
                <ChartCard title="Pending Aging" subtitle="How long orders have been pending" isDark={isDarkMode} style={chartStyle('do-aging')}>
                    <LazyMount style={{ height: '250px' }}>
                        <EChart option={barOption({
                            labels: ['0–7 days', '8–30 days', '30+ days'],
                            series: [{
                                name: 'Pending',
                                data: [
                                    { value: deliveryOrderStats.aging?.d0_7 || 0, itemStyle: { color: '#3B82F6' } },
                                    { value: deliveryOrderStats.aging?.d8_30 || 0, itemStyle: { color: '#F59E0B' } },
                                    { value: deliveryOrderStats.aging?.d31plus || 0, itemStyle: { color: '#EF4444' } },
                                ],
                            }],
                            isDark: isDarkMode,
                        })} />
                    </LazyMount>
                </ChartCard>

                {/* Top Items by Value */}
                <ChartCard title="Top Items by Value" subtitle="Highest order value this month" isDark={isDarkMode} style={chartStyle('do-top-items')}>
                    {(deliveryOrderStats.topItems || []).length > 0 ? (
                        <LazyMount style={{ height: '250px' }}>
                            <EChart option={barOption({
                                horizontal: true,
                                labels: (deliveryOrderStats.topItems || []).map(i => i.name),
                                series: [{
                                    name: 'Value',
                                    color: isDarkMode ? '#A855F7' : '#9333EA',
                                    data: (deliveryOrderStats.topItems || []).map(i => i.amount),
                                }],
                                isDark: isDarkMode,
                                tooltipFormatter: (params) => {
                                    const p = Array.isArray(params) ? params[0] : params;
                                    return `${p.name}: ₹${formatNumberWithCommas(p.value)}`;
                                },
                            })} />
                        </LazyMount>
                    ) : <EmptyState message="No item data this month" isDark={isDarkMode} className="py-16" />}
                </ChartCard>
                </>
                )}

                {/* Quotations New Trend */}
                {hasQuotationNewAccess && (
                <>
                <TrendCard
                    title="Quotations (New) Trend (Last 7 Days)"
                    subtitle="Company-wide quotations created per day"
                    labels={(quotationStats.weeklyData || []).map(d => d.label)}
                    data={(quotationStats.weeklyData || []).map(d => d.count)}
                    color={isDarkMode ? '#60A5FA' : '#3B82F6'}
                    isDark={isDarkMode}
                    style={chartStyle('qn-trend')}
                />
                </>
                )}

                {/* Tally Quotations Trend */}
                {hasTallyQuotationAccess && (
                <>
                <TrendCard
                    title="Tally Quotations Trend (Last 7 Days)"
                    subtitle="Company-wide tally quotations created per day"
                    labels={(tallyQuotationStats.weeklyData || []).map(d => d.label)}
                    data={(tallyQuotationStats.weeklyData || []).map(d => d.count)}
                    color={isDarkMode ? '#60A5FA' : '#3B82F6'}
                    isDark={isDarkMode}
                    style={chartStyle('tq-trend')}
                />
                </>
                )}
            </div>
            )}

            {/* My Performance - personalized stats */}
            {(hasDeliveryOrderAccess || hasQuotationNewAccess || hasTallyQuotationAccess || hasVisitorAccess || personalStats?.reminders) && showSection('myPerformance') && (
            <div className={sectionClass('myPerformance')} style={sectionStyle('myPerformance')}>
                <h2 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Performance
                </h2>
                {personalStatsLoading && !personalStats ? (
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading…</p>
                ) : (
                    <>
            <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Company</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {/* Delivery Orders Card */}
                {hasDeliveryOrderAccess && (
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6 hover:shadow-xl transition-all duration-300`}>
                    <div className="flex items-center justify-between mb-4">
                        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                            <FaTruck className={`text-2xl ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                        </div>
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                            This Month
                        </span>
                    </div>
                    <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {deliveryOrderStats.total}
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                        Delivery Orders
                    </p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center">
                            <div className="text-emerald-500 font-semibold">{deliveryOrderStats.completed}</div>
                            <div className="text-emerald-400 text-[10px] font-medium">
                                {deliveryOrderStats.total > 0 ? `${((deliveryOrderStats.completed / deliveryOrderStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Completed</div>
                        </div>
                        <div className="text-center">
                            <div className="text-amber-500 font-semibold">{deliveryOrderStats.pending}</div>
                            <div className="text-amber-400 text-[10px] font-medium">
                                {deliveryOrderStats.total > 0 ? `${((deliveryOrderStats.pending / deliveryOrderStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Pending</div>
                        </div>
                        <div className="text-center">
                            <div className="text-red-500 font-semibold">{deliveryOrderStats.cancelled}</div>
                            <div className="text-red-400 text-[10px] font-medium">
                                {deliveryOrderStats.total > 0 ? `${((deliveryOrderStats.cancelled / deliveryOrderStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Cancelled</div>
                        </div>
                    </div>
                </div>
                )}

                {/* Quotations New Card */}
                {hasQuotationNewAccess && (
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6 hover:shadow-xl transition-all duration-300`}>
                    <div className="flex items-center justify-between mb-4">
                        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
                            <FaFileAlt className={`text-2xl ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                        </div>
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                            This Month
                        </span>
                    </div>
                    <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {quotationStats.total}
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                        Quotations
                    </p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center">
                            <div className="text-amber-500 font-semibold">{quotationStats.pending}</div>
                            <div className="text-amber-400 text-[10px] font-medium">
                                {quotationStats.total > 0 ? `${((quotationStats.pending / quotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Pending</div>
                        </div>
                        <div className="text-center">
                            <div className="text-emerald-500 font-semibold">{quotationStats.completed}</div>
                            <div className="text-emerald-400 text-[10px] font-medium">
                                {quotationStats.total > 0 ? `${((quotationStats.completed / quotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Completed</div>
                        </div>
                        <div className="text-center">
                            <div className="text-blue-500 font-semibold">{quotationStats.partialComplete}</div>
                            <div className="text-blue-400 text-[10px] font-medium">
                                {quotationStats.total > 0 ? `${((quotationStats.partialComplete / quotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Partial</div>
                        </div>
                        <div className="text-center">
                            <div className="text-purple-500 font-semibold">{quotationStats.refFollow}</div>
                            <div className="text-purple-400 text-[10px] font-medium">
                                {quotationStats.total > 0 ? `${((quotationStats.refFollow / quotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Ref Follow</div>
                        </div>
                        <div className="text-center col-span-2">
                            <div className="text-red-500 font-semibold">{quotationStats.cancelled}</div>
                            <div className="text-red-400 text-[10px] font-medium">
                                {quotationStats.total > 0 ? `${((quotationStats.cancelled / quotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Cancelled</div>
                        </div>
                    </div>
                </div>
                )}

                {/* Tally Quotations Card */}
                {hasTallyQuotationAccess && (
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6 hover:shadow-xl transition-all duration-300`}>
                    <div className="flex items-center justify-between mb-4">
                        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-green-900/30' : 'bg-green-100'}`}>
                            <FaFileInvoice className={`text-2xl ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                        </div>
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                            This Month
                        </span>
                    </div>
                    <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {tallyQuotationStats.total}
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                        Tally Quotations
                    </p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center">
                            <div className="text-amber-500 font-semibold">{tallyQuotationStats.pending}</div>
                            <div className="text-amber-400 text-[10px] font-medium">
                                {tallyQuotationStats.total > 0 ? `${((tallyQuotationStats.pending / tallyQuotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Pending</div>
                        </div>
                        <div className="text-center">
                            <div className="text-emerald-500 font-semibold">{tallyQuotationStats.completed}</div>
                            <div className="text-emerald-400 text-[10px] font-medium">
                                {tallyQuotationStats.total > 0 ? `${((tallyQuotationStats.completed / tallyQuotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Completed</div>
                        </div>
                        <div className="text-center">
                            <div className="text-blue-500 font-semibold">{tallyQuotationStats.partialComplete}</div>
                            <div className="text-blue-400 text-[10px] font-medium">
                                {tallyQuotationStats.total > 0 ? `${((tallyQuotationStats.partialComplete / tallyQuotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Partial</div>
                        </div>
                        <div className="text-center">
                            <div className="text-purple-500 font-semibold">{tallyQuotationStats.refFollow}</div>
                            <div className="text-purple-400 text-[10px] font-medium">
                                {tallyQuotationStats.total > 0 ? `${((tallyQuotationStats.refFollow / tallyQuotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Ref Follow</div>
                        </div>
                        <div className="text-center col-span-2">
                            <div className="text-red-500 font-semibold">{tallyQuotationStats.cancelled}</div>
                            <div className="text-red-400 text-[10px] font-medium">
                                {tallyQuotationStats.total > 0 ? `${((tallyQuotationStats.cancelled / tallyQuotationStats.total) * 100).toFixed(1)}%` : '0%'}
                            </div>
                            <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Cancelled</div>
                        </div>
                    </div>
                </div>
                )}
            </div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>My Performance</h3>
                            <SegmentedTabs
                                isDark={isDarkMode}
                                value={personalPeriod}
                                onChange={setPersonalPeriod}
                                options={[{ key: 'month', label: 'Current Month' }, { key: '14d', label: 'Last 14 Days' }]}
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {hasDeliveryOrderAccess && renderPersonalModuleCard('Delivery Orders', personalStats?.deliveryOrders, 'deliveryOrders')}
                            {hasQuotationNewAccess && renderPersonalModuleCard('Quotation New', personalStats?.quotationNew, 'quotationNew')}
                            {hasTallyQuotationAccess && renderPersonalModuleCard('Tally Quotations', personalStats?.tallyQuotation, 'tallyQuotation')}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                            {hasVisitorAccess && renderPersonalVisitorCard()}
                            {renderPersonalRemindersCard()}
                        </div>
                    </>
                )}
            </div>
            )}

            {/* Tally Items Management Section */}
            {hasTallyItemsAccess && showSection('tallyItems') && (
            <div className={sectionClass('tallyItems')} style={sectionStyle('tallyItems')}>
                <h2 className={`text-2xl font-bold mb-6 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <FaBox className="text-indigo-500" /> Tally Items Management
                </h2>

                {/* Tally Item Usage Report Tracker */}
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6 mb-8`}>
                    <div className="flex items-center space-x-3 mb-6">
                        <FaChartBar className={`text-2xl ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                        <div>
                            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                Tally Item Lifecycle & Usage Report
                            </h3>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-0.5`}>
                                Track Tally Quotations, Proforma Invoices, and Delivery Orders for any inventory item.
                            </p>
                        </div>
                    </div>

                    {/* Date Range Selectors - Defaulting to Current Month */}
                    <div className={`p-4 border rounded-xl mb-6 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between ${
                        isDarkMode ? 'bg-gray-750/50 border-gray-650/40' : 'bg-gray-50 border-gray-200'
                    }`}>
                        <div className="flex items-center space-x-2">
                            <FaCalendarAlt className={`text-lg ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                            <div>
                                <p className={`text-sm font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Usage Date Range Filter:
                                </p>
                                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-550'} mt-0.5`}>
                                    Filter and fetch Tally Quotations, Proforma Invoices, and Delivery Orders matching selected dates.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 w-full md:w-auto">
                            <div className="flex flex-col">
                                <label className={`text-[10px] font-bold uppercase mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Start Date</label>
                                <input
                                    type="date"
                                    value={reportDateRange.startDate}
                                    onChange={(e) => setReportDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                                    className={`px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold ${
                                        isDarkMode 
                                            ? 'bg-gray-700 border-gray-600 text-white' 
                                            : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                />
                            </div>
                            <div className="flex flex-col">
                                <label className={`text-[10px] font-bold uppercase mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>End Date</label>
                                <input
                                    type="date"
                                    value={reportDateRange.endDate}
                                    onChange={(e) => setReportDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                                    className={`px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold ${
                                        isDarkMode 
                                            ? 'bg-gray-700 border-gray-600 text-white' 
                                            : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Search Autocomplete Bar - Hide after selection "after selection, no search" */}
                    {!selectedReportItem ? (
                        <div className="relative mb-6">
                            <div className="flex items-center">
                                <div className="relative flex-grow">
                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                        <FaSearch className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                                    </span>
                                    <input
                                        type="text"
                                        value={reportSearchTerm}
                                        placeholder="Type tally item name to search lifecycle usage..."
                                        onChange={(e) => setReportSearchTerm(e.target.value)}
                                        className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                                            isDarkMode 
                                                ? 'bg-gray-750 border-gray-600 text-white placeholder-gray-400' 
                                                : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'
                                        }`}
                                    />
                                    {reportSearchLoading && (
                                        <span className="absolute inset-y-0 right-0 flex items-center pr-3">
                                            <svg className="animate-spin h-5 w-5 text-indigo-500" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Dropdown Results list */}
                            {reportSearchResults.length > 0 && (
                                <div className={`absolute z-10 w-full mt-1.5 rounded-lg shadow-2xl border max-h-60 overflow-y-auto ${
                                    isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-950'
                                }`}>
                                    {reportSearchResults.map((item) => (
                                        <div
                                            key={item._id}
                                            onClick={() => handleSelectReportItem(item)}
                                            className={`px-4 py-3 cursor-pointer text-sm font-medium border-b last:border-b-0 transition-colors flex justify-between items-center ${
                                                isDarkMode ? 'border-gray-700/60 hover:bg-gray-700' : 'border-gray-105 hover:bg-blue-50/50'
                                            }`}
                                        >
                                            <div>
                                                <p className="font-semibold">{item.Name}</p>
                                                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-555'}`}>
                                                    🏷️ Brand: {item.brand?.name || 'No Brand'} {item.partNo ? `| 🔢 Part No: ${item.partNo}` : ''}
                                                </p>
                                            </div>
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                                isDarkMode ? 'bg-indigo-950/40 text-indigo-300' : 'bg-indigo-50 text-indigo-700'
                                            }`}>
                                                Stock: {item.closingBalance || 0}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Premium Static Info pill with remove option instead of Search Bar when Selected ("after selection, no search") */
                        <div className={`p-4 border rounded-xl flex items-center justify-between shadow-sm mb-6 ${
                            isDarkMode 
                                ? 'bg-indigo-950/20 border-indigo-500/40 text-indigo-200' 
                                : 'bg-indigo-50 border-indigo-200 text-indigo-800'
                        }`}>
                            <div className="flex items-center space-x-3">
                                <FaBox className="text-xl" />
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-wider opacity-75">Selected Item Locked:</p>
                                    <h4 className="text-base font-black truncate">{selectedReportItem.Name}</h4>
                                    <p className="text-[10px] opacity-70 mt-0.5">
                                        Traces mapped successfully | Select Date Range or click right button to change item.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3">
                                <button
                                    onClick={handleReviewPDFReport}
                                    title="Review Trace Report"
                                    className={`px-4 py-2.5 rounded-lg font-bold text-xs transition duration-150 flex items-center space-x-1.5 shadow-sm border ${
                                        isDarkMode 
                                            ? 'bg-indigo-950/30 text-indigo-400 border-indigo-900/60 hover:bg-indigo-950/50' 
                                            : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
                                    }`}
                                >
                                    <FaPrint className="text-[11px]" />
                                    <span>Review Report</span>
                                </button>
                                <button
                                    onClick={() => {
                                        setSelectedReportItem(null);
                                        setReportData(null);
                                        setReportSearchTerm('');
                                    }}
                                    className={`px-4 py-2.5 rounded-lg font-bold text-xs transition duration-150 flex items-center space-x-1.5 shadow-sm border ${
                                        isDarkMode 
                                            ? 'bg-red-950/30 text-red-400 border-red-900/60 hover:bg-red-950/50' 
                                            : 'bg-red-50 text-red-720 border-red-200 hover:bg-red-100'
                                    }`}
                                >
                                    <FaTimes className="text-[10px]" />
                                    <span>Change Item</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Report Output Loader */}
                    {reportLoading && (
                        <div className="flex flex-col items-center justify-center py-12">
                            <svg className="animate-spin h-8 w-8 text-indigo-500 mb-3" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            <p className={`text-sm font-semibold animate-pulse ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                Gathering document references and compiles usage traces...
                            </p>
                        </div>
                    )}

                    {/* Report Details Wrapper */}
                    {!reportLoading && reportData && (
                        <div className="transition-all duration-200 animate-fadeIn">
                            {/* Selected Item Summary Header */}
                            <div className={`p-4 rounded-xl mb-6 flex flex-col md:flex-row justify-between items-start md:items-center ${
                                isDarkMode ? 'bg-gray-700/40 border border-gray-600/50' : 'bg-gray-50 border border-gray-150'
                            }`}>
                                <div className="mb-4 md:mb-0">
                                    <h4 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                        {reportData.item.Name}
                                    </h4>
                                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                                        <p>🏷️ Brand: <span className="font-semibold">{reportData.item.brand}</span></p>
                                        <p>🔢 Part Number: <span className="font-semibold">{reportData.item.partNo}</span></p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className={`text-[10px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Tally Closing Stock:
                                    </p>
                                    <p className="text-2xl font-black text-indigo-500">
                                        {reportData.item.closingBalance} pcs
                                    </p>
                                </div>
                            </div>

                            {/* Sub-tab Selection */}
                            <div className="flex border-b border-gray-650/40 mb-6">
                                <button
                                    onClick={() => setReportActiveTab('pi')}
                                    className={`px-5 py-3 text-sm font-bold border-b-2 text-center transition-all ${
                                        reportActiveTab === 'pi'
                                            ? 'border-indigo-500 text-indigo-500 font-extrabold'
                                            : `border-transparent ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'}`
                                    }`}
                                >
                                    📄 Proforma Invoices ({reportData.proformaInvoices.length})
                                </button>
                                <button
                                    onClick={() => setReportActiveTab('tq')}
                                    className={`px-5 py-3 text-sm font-bold border-b-2 text-center transition-all ${
                                        reportActiveTab === 'tq'
                                            ? 'border-indigo-500 text-indigo-500 font-extrabold'
                                            : `border-transparent ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'}`
                                    }`}
                                >
                                    📝 Tally Quotations ({reportData.tallyQuotations.length})
                                </button>
                                <button
                                    onClick={() => setReportActiveTab('do')}
                                    className={`px-5 py-3 text-sm font-bold border-b-2 text-center transition-all ${
                                        reportActiveTab === 'do'
                                            ? 'border-indigo-500 text-indigo-500 font-extrabold'
                                            : `border-transparent ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'}`
                                    }`}
                                >
                                    🚚 Delivery Orders ({reportData.deliveryOrders.length})
                                </button>
                            </div>

                            {/* Inner usage sub-grid */}
                            <div>
                                {/* Subtab PI rendering */}
                                {reportActiveTab === 'pi' && (
                                    reportData.proformaInvoices.length > 0 ? (
                                        <div className="space-y-4 max-h-[450px] overflow-y-auto pr-1">
                                            {reportData.proformaInvoices.map((pi) => (
                                                <div key={pi._id} className={`p-4 border rounded-xl shadow-sm ${
                                                    isDarkMode ? 'bg-gray-800/80 border-gray-700/60' : 'bg-white border-gray-200'
                                                }`}>
                                                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3">
                                                        <div>
                                                            <p className={`text-base font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                                                Invoice #{pi.invoiceNumber}
                                                            </p>
                                                            <p className="text-xs text-gray-500 mt-0.5">
                                                                📅 Invoice Date: {new Date(pi.invoiceDate).toLocaleDateString()} | 👤 Creator: {pi.createdBy}
                                                            </p>
                                                            {pi.recipient && (
                                                                <p className={`text-xs mt-1 font-semibold ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                                                                    🏢 Customer: {pi.recipient.name} ({pi.recipient.type})
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="text-right mt-2 md:mt-0">
                                                            <p className="text-sm font-black text-indigo-500">
                                                                {formatCurrency(pi.totalAmount || 0)}
                                                            </p>
                                                            <div className="flex space-x-1.5 mt-1.5 justify-end">
                                                                <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${
                                                                    pi.status === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                                }`}>{pi.status}</span>
                                                                <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${
                                                                    pi.deliveryStatus === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                                                                }`}>{pi.deliveryStatus || 'PENDING'}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Items sub-view matching list */}
                                                    <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-gray-750/70' : 'border-gray-100'} space-y-2`}>
                                                        <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                            🔄 Matching Item Entry:
                                                        </p>
                                                        {pi.matchingItems.map((li, idx) => (
                                                            <div key={idx} className={`flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 rounded-lg text-xs font-semibold space-y-2 sm:space-y-0 border ${
                                                                isDarkMode ? 'bg-gray-700/40 border-gray-600/30' : 'bg-gray-50 border border-gray-150'
                                                            }`}>
                                                                <div className="flex flex-col">
                                                                    <span className={`${isDarkMode ? 'text-gray-100' : 'text-gray-950'} font-bold`}>{li.printName || li.Name || 'Tally Item'}</span>
                                                                    {li.customName && (
                                                                        <span className={`text-[10px] mt-0.5 font-normal ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>
                                                                            ✏️ Custom: {li.customName}
                                                                        </span>
                                                                    )}
                                                                    {li.printName && li.Name && li.printName !== li.Name && (
                                                                        <span className="text-[10px] text-gray-500 font-normal mt-0.5">Line name: {li.Name}</span>
                                                                    )}
                                                                    <div className="flex flex-wrap gap-2 text-[10px] text-gray-500 mt-1 font-normal">
                                                                        {li.offerPrice && <span>Rate: {formatCurrency(li.offerPrice)}</span>}
                                                                        {li.allocatedQuantity > 0 && <span className="text-green-550 border border-green-500/20 bg-green-500/10 px-1.5 py-0.5 rounded-full font-bold">Allocated: {li.allocatedQuantity} pcs</span>}
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center space-x-4">
                                                                    <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                                                                        Qty: <span className="font-extrabold text-blue-500">{li.quantity}</span> pcs
                                                                    </p>
                                                                    <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                                                                        Amount: <span className="font-extrabold text-indigo-500">{formatCurrency(li.amount || (li.quantity * li.offerPrice) || 0)}</span>
                                                                    </p>
                                                                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${
                                                                        li.stockStatus === 'to-be-billed' || li.stockStatus === 'ready-to-sale'
                                                                          ? 'bg-blue-105 text-blue-800 border border-blue-200'
                                                                          : li.stockStatus === 'to-be-reserved'
                                                                          ? 'bg-yellow-105 text-yellow-850 border border-yellow-200'
                                                                          : 'bg-green-105 text-green-800 border border-green-200'
                                                                    }`}>
                                                                        {li.stockStatus?.replace('-', ' ').toUpperCase()}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-10 bg-gray-50/5 rounded-lg border border-dashed border-gray-600/30">
                                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                No Proforma Invoice references found matching selected date range.
                                            </p>
                                        </div>
                                    )
                                )}

                                {/* Subtab TQ rendering */}
                                {reportActiveTab === 'tq' && (
                                    reportData.tallyQuotations.length > 0 ? (
                                        <div className="space-y-4 max-h-[450px] overflow-y-auto pr-1">
                                            {reportData.tallyQuotations.map((tq) => (
                                                <div key={tq._id} className={`p-4 border rounded-xl shadow-sm ${
                                                    isDarkMode ? 'bg-gray-800/80 border-gray-700/60' : 'bg-white border-gray-200'
                                                }`}>
                                                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3">
                                                        <div>
                                                            <p className={`text-base font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                                                Quotation #{tq.quotationId}
                                                            </p>
                                                            <p className="text-xs text-gray-500 mt-0.5">
                                                                📅 Created Date: {new Date(tq.createdAt).toLocaleDateString()} | 👤 Creator: {tq.createdBy}
                                                            </p>
                                                            {tq.recipient && (
                                                                <p className={`text-xs mt-1 font-semibold ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                                                                    🏢 Recipient: {tq.recipient.name} ({tq.recipient.type})
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="text-right mt-2 md:mt-0">
                                                            <p className="text-sm font-black text-indigo-500">
                                                                {formatCurrency(tq.totalAmount || 0)}
                                                            </p>
                                                            <span className={`inline-block mt-1.5 px-2.5 py-0.5 text-[10px] font-bold rounded-full ${
                                                                tq.status === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                            }`}>{tq.status}</span>
                                                        </div>
                                                    </div>

                                                    {/* Items sub-view matching list */}
                                                    <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-gray-750/70' : 'border-gray-100'} space-y-2`}>
                                                        <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                            🔄 Matching Item Entry:
                                                        </p>
                                                        {tq.matchingItems.map((li, idx) => (
                                                            <div key={idx} className={`flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 rounded-lg text-xs font-semibold space-y-2 sm:space-y-0 border ${
                                                                isDarkMode ? 'bg-gray-700/40 border-gray-600/30' : 'bg-gray-50 border border-gray-150'
                                                            }`}>
                                                                <div className="flex flex-col">
                                                                        <span className={`${isDarkMode ? 'text-gray-100' : 'text-gray-950'} font-bold`}>{li.printName || li.Name || 'Tally Item'}</span>
                                                                        {li.customName && (
                                                                            <span className={`text-[10px] mt-0.5 font-normal ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>
                                                                                ✏️ Custom: {li.customName}
                                                                            </span>
                                                                        )}
                                                                        {li.printName && li.Name && li.printName !== li.Name && (
                                                                            <span className="text-[10px] text-gray-500 font-normal mt-0.5">Line name: {li.Name}</span>
                                                                        )}
                                                                        {li.offerPrice && (
                                                                            <p className="text-[10px] text-gray-500 mt-1 font-normal">
                                                                                Offer Rate: {formatCurrency(li.offerPrice)}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                <div className="flex items-center space-x-6">
                                                                    <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                                                                        Qty: <span className="font-extrabold text-blue-500">{li.quantity}</span> pcs
                                                                    </p>
                                                                    <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                                                                        Total Value: <span className="font-extrabold text-indigo-500">{formatCurrency(li.amount || (li.quantity * li.offerPrice) || 0)}</span>
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-10 bg-gray-50/5 rounded-lg border border-dashed border-gray-600/30">
                                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                No Tally Quotation references found matching selected date range.
                                            </p>
                                        </div>
                                    )
                                )}

                                {/* Subtab DO rendering */}
                                {reportActiveTab === 'do' && (
                                    reportData.deliveryOrders.length > 0 ? (
                                        <div className="space-y-4 max-h-[450px] overflow-y-auto pr-1">
                                            {reportData.deliveryOrders.map((doDoc) => (
                                                <div key={doDoc._id} className={`p-4 border rounded-xl shadow-sm ${
                                                    isDarkMode ? 'bg-gray-800/80 border-gray-700/60' : 'bg-white border-gray-200'
                                                }`}>
                                                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3">
                                                        <div>
                                                            <p className={`text-base font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                                                DO Number: #{doDoc.doNumber}
                                                            </p>
                                                            <p className="text-xs text-gray-500 mt-0.5">
                                                                📅 Date: {new Date(doDoc.createdAt).toLocaleDateString()} | 👤 Creator: {doDoc.createdBy}
                                                            </p>
                                                            {doDoc.recipient && (
                                                                <p className={`text-xs mt-1 font-semibold ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                                                                    🏢 Recipient: {doDoc.recipient.name} ({doDoc.recipient.type})
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="text-right mt-2 md:mt-0">
                                                            <p className="text-sm font-black text-indigo-500">
                                                                {formatCurrency(doDoc.totalAmountWithGST || 0)}
                                                            </p>
                                                            <span className={`inline-block mt-1.5 px-2.5 py-0.5 text-[10px] font-bold rounded-full ${
                                                                doDoc.status === 'Completed' || doDoc.status === 'COMPLETED' ? 'bg-green-100 text-green-800 shadow-sm border border-green-200' : 'bg-yellow-100 text-yellow-800 border'
                                                            }`}>{doDoc.status}</span>
                                                        </div>
                                                    </div>

                                                    {/* Items sub-view matching list */}
                                                    <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-gray-750/70' : 'border-gray-100'} space-y-2`}>
                                                        <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-550'}`}>
                                                            🔄 Matching Item Entry:
                                                        </p>
                                                        {doDoc.matchingItems.map((li, idx) => (
                                                            <div key={idx} className={`flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 rounded-lg text-xs font-semibold space-y-2 sm:space-y-0 border ${
                                                                isDarkMode ? 'bg-gray-700/40 border border-gray-600/30' : 'bg-gray-50 border border-gray-150'
                                                            }`}>
                                                                <div className="flex flex-col">
                                                                    <span className={`${isDarkMode ? 'text-gray-100' : 'text-gray-950'} font-bold`}>{li.Name || 'Tally Item'}</span>
                                                                    {li.description && (
                                                                        <span className={`text-[10px] mt-0.5 font-normal ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                                                                            📝 {li.description}
                                                                        </span>
                                                                    )}
                                                                    {li.rate && (
                                                                        <p className="text-[10px] text-gray-500 mt-1 font-normal">
                                                                            DO Rate: {formatCurrency(li.rate)}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center space-x-6">
                                                                    <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                                                                        Qty: <span className="font-extrabold text-blue-500">{li.quantity}</span> pcs
                                                                    </p>
                                                                    <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                                                                        Line Total: <span className="font-extrabold text-indigo-500">{formatCurrency(li.amountAfterDiscount || (li.quantity * li.rate) || 0)}</span>
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-10 bg-gray-50/5 rounded-lg border border-dashed border-gray-600/30">
                                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-555'}`}>
                                                No Delivery Order references found matching selected date range.
                                            </p>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    )}

                    {!selectedReportItem && (
                        <div className={`text-center py-12 bg-gray-50/5 rounded-xl border border-dashed ${isDarkMode ? 'border-gray-700/60' : 'border-gray-200'}`}>
                            <FaBox className={`text-4xl mx-auto ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                            <p className={`text-sm font-semibold mt-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                Select a Tally Item above to generate complete usage traces
                            </p>
                        </div>
                    )}
                </div>

                {/* Reserved Tally Items Report */}
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6 mb-8`}>
                    <div className="flex items-center space-x-3 mb-6">
                        <FaBox className={`text-2xl ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                        <div>
                            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                Reserved Tally Items Report
                            </h3>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-0.5`}>
                                All Tally items currently reserved across pending/partial Proforma Invoices.
                            </p>
                        </div>
                    </div>

                    {/* Loading */}
                    {reservedItemsLoading && (
                        <div className="flex flex-col items-center justify-center py-12">
                            <svg className="animate-spin h-8 w-8 text-indigo-500 mb-3" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            <p className={`text-sm font-semibold animate-pulse ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                Loading reserved tally items...
                            </p>
                        </div>
                    )}

                    {/* Summary Table */}
                    {!reservedItemsLoading && reservedItemsData && (
                        <div className="space-y-6">
                            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-indigo-950/20 border border-indigo-500/40' : 'bg-indigo-50 border border-indigo-200'}`}>
                                <p className={`text-sm font-bold ${isDarkMode ? 'text-indigo-300' : 'text-indigo-800'}`}>
                                    Total Reserved Items: <span className="text-2xl ml-2">{reservedItemsData.count}</span>
                                    {reservedSearchLower && (
                                        <span className={`ml-3 text-xs font-medium ${isDarkMode ? 'text-indigo-200' : 'text-indigo-700'}`}>
                                            ({reservedFiltered.length} matching)
                                        </span>
                                    )}
                                </p>
                            </div>

                            {/* Search + page size */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <input
                                    type="text"
                                    value={reservedSearch}
                                    onChange={(e) => { setReservedSearch(e.target.value); setReservedPage(1); }}
                                    placeholder="Search by item name, brand or part no..."
                                    className={`w-full sm:max-w-md px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'}`}
                                />
                                <div className="flex items-center gap-2 text-xs">
                                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Rows per page</span>
                                    <select
                                        value={reservedPageSize}
                                        onChange={(e) => { setReservedPageSize(Number(e.target.value)); setReservedPage(1); }}
                                        className={`px-2 py-1.5 rounded-lg border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-800'}`}
                                    >
                                        {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className={`w-full text-xs border-collapse ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                    <thead>
                                        <tr className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                                            <th className="text-left p-3 rounded-tl-lg">Item Name</th>
                                            <th className="text-left p-3">Brand</th>
                                            <th className="text-right p-3">Closing Stock</th>
                                            <th className="text-right p-3">Total Reserved</th>
                                            <th className="text-center p-3">PI Lines</th>
                                            <th className="text-center p-3 rounded-tr-lg">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reservedPagedItems.map((item) => (
                                            <tr
                                                key={item.itemId}
                                                className={`border-b ${isDarkMode ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-200 hover:bg-gray-50'} ${selectedReservedItem?.itemId === item.itemId ? (isDarkMode ? 'bg-indigo-950/30' : 'bg-indigo-50') : ''}`}
                                            >
                                                <td className="p-3 font-semibold">{item.itemName}</td>
                                                <td className="p-3">{item.brandName}</td>
                                                <td className="p-3 text-right">{safeNumber(item.closingBalance)} {item.unit || 'pcs'}</td>
                                                <td className="p-3 text-right font-bold text-indigo-500">
                                                    <div className="flex flex-col items-end gap-0.5">
                                                        {formatReservedQuantityWithArea(item).map((quantityText) => (
                                                            <span key={quantityText}>{quantityText}</span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="p-3 text-center">{safeNumber(item.lineCount)}</td>
                                                <td className="p-3 text-center">
                                                    <button
                                                        onClick={() => handleSelectReservedItem(item)}
                                                        className={`px-3 py-1.5 rounded-lg font-bold text-[10px] transition duration-150 ${
                                                            isDarkMode
                                                                ? 'bg-indigo-950/40 text-indigo-300 border border-indigo-800 hover:bg-indigo-950/60'
                                                                : 'bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100'
                                                        }`}
                                                    >
                                                        View Details
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {reservedPagedItems.length === 0 && (
                                            <tr>
                                                <td colSpan={6} className={`p-6 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                    No reserved items match your search.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
                                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                                    {reservedFiltered.length === 0
                                        ? 'Showing 0 of 0'
                                        : `Showing ${(reservedSafePage - 1) * reservedPageSize + 1}–${Math.min(reservedSafePage * reservedPageSize, reservedFiltered.length)} of ${reservedFiltered.length}`}
                                </span>
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        onClick={() => setReservedPage(1)}
                                        disabled={reservedSafePage <= 1}
                                        className={`px-2.5 py-1.5 rounded-lg border font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                                    >
                                        «
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setReservedPage(p => Math.max(1, p - 1))}
                                        disabled={reservedSafePage <= 1}
                                        className={`px-2.5 py-1.5 rounded-lg border font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                                    >
                                        ‹
                                    </button>
                                    <span className={`px-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Page {reservedSafePage} / {reservedTotalPages}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setReservedPage(p => Math.min(reservedTotalPages, p + 1))}
                                        disabled={reservedSafePage >= reservedTotalPages}
                                        className={`px-2.5 py-1.5 rounded-lg border font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                                    >
                                        ›
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setReservedPage(reservedTotalPages)}
                                        disabled={reservedSafePage >= reservedTotalPages}
                                        className={`px-2.5 py-1.5 rounded-lg border font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                                    >
                                        »
                                    </button>
                                </div>
                            </div>

                            {/* Selected Item Details Modal */}
                            {showReservedItemModal && selectedReservedItem && (
                                <div
                                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                                    onClick={(e) => {
                                        if (e.target === e.currentTarget) closeReservedItemModal();
                                    }}
                                >
                                    <div className={`w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                                        {/* Modal Header */}
                                        <div className={`flex items-center justify-between p-5 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                                            <div>
                                                <h4 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                    {selectedReservedItem.itemName}
                                                </h4>
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    Brand: {selectedReservedItem.brandName} | Total Reserved:{" "}
                                                    {reservedItemDetails?.totals?.reservedByUnit && Object.entries(reservedItemDetails.totals.reservedByUnit).length > 0 ? (
                                                        formatReservedQuantityWithArea(reservedItemDetails.totals)
                                                            .join(', ')
                                                    ) : (
                                                        `${safeNumber(selectedReservedItem.totalReservedQuantity)} ${selectedReservedItem.unit || 'pcs'}`
                                                    )}
                                                </p>
                                            </div>
                                            <button
                                                onClick={closeReservedItemModal}
                                                className={`px-4 py-2 rounded-lg font-bold text-xs transition duration-150 ${
                                                    isDarkMode
                                                        ? 'bg-red-950/30 text-red-400 border border-red-900/60 hover:bg-red-950/50'
                                                        : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
                                                }`}
                                            >
                                                Close Details
                                            </button>
                                        </div>

                                        {/* Modal Body */}
                                        <div className="p-5 overflow-y-auto max-h-[calc(90vh-80px)]">
                                            {reservedItemDetailsLoading && (
                                                <div className="flex justify-center py-10">
                                                    <svg className="animate-spin h-8 w-8 text-indigo-500" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                    </svg>
                                                </div>
                                            )}

                                            {!reservedItemDetailsLoading && reservedItemDetails && (
                                                <div className="space-y-5">
                                                    {/* Totals summary grouped by unit */}
                                                    {reservedItemDetails.totals && (
                                                        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-indigo-950/10 border-indigo-500/30' : 'bg-indigo-50 border-indigo-200'}`}>
                                                            <p className={`text-xs font-bold uppercase mb-3 ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                                                                Total Reserved
                                                            </p>
                                                            <div className="flex flex-wrap gap-2">
                                                                {reservedItemDetails.totals.reservedByUnit && Object.entries(reservedItemDetails.totals.reservedByUnit).length > 0 ? (
                                                                    formatReservedQuantityWithArea(reservedItemDetails.totals).map((quantityText) => (
                                                                        <span
                                                                            key={quantityText}
                                                                            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-black ${
                                                                                isDarkMode
                                                                                    ? 'bg-indigo-950/40 text-indigo-200 border border-indigo-800'
                                                                                    : 'bg-white text-indigo-800 border border-indigo-200'
                                                                            }`}
                                                                        >
                                                                            {quantityText}
                                                                        </span>
                                                                    ))
                                                                ) : (
                                                                    <span className={`text-base font-black ${isDarkMode ? 'text-indigo-200' : 'text-indigo-900'}`}>
                                                                        {safeNumber(reservedItemDetails.totals.totalReservedQuantity)} pcs
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="space-y-4">
                                                        {reservedItemDetails.reservedDetails.map((detail, idx) => (
                                                            <div key={idx} className={`p-4 rounded-xl border ${isDarkMode ? 'bg-gray-700/40 border-gray-600/30' : 'bg-gray-50 border-gray-150'}`}>
                                                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
                                                                    <div>
                                                                        <p className={`text-base font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                                                            PI #{detail.piId}
                                                                        </p>
                                                                        <p className="text-xs text-gray-500 mt-0.5">
                                                                            Reserved: {detail.dateReserved ? new Date(detail.dateReserved).toLocaleDateString() : '-'} | Created By: {detail.createdBy || 'Unknown'}
                                                                        </p>
                                                                        <p className="text-xs text-gray-500">
                                                                            Recipient: {detail.recipientName || 'Unknown'}
                                                                        </p>
                                                                    </div>
                                                                    <div className="text-right mt-2 sm:mt-0">
                                                                        <p className="text-xs text-gray-500">Rate: {formatCurrency(detail.offerPrice)}</p>
                                                                        <p className="text-xs text-gray-500">Amount: {formatCurrency(detail.amount)}</p>
                                                                    </div>
                                                                </div>

                                                                {/* Reserved quantity with alternate representations */}
                                                                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
                                                                    <p className="text-[10px] font-bold uppercase text-indigo-500 mb-1">Reserved Quantity</p>
                                                                    <p className={`text-lg font-black ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                                                                        {formatLineReservedQuantity(detail)}
                                                                    </p>
                                                                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                                                                        {detail.reservedBoxes > 0 && (
                                                                            <span className="text-xs text-gray-500">
                                                                                {safeNumber(detail.reservedBoxes)} boxes
                                                                            </span>
                                                                        )}
                                                                        {detail.reservedPieces > 0 && (
                                                                            <span className="text-xs text-gray-500">
                                                                                {safeNumber(detail.reservedPieces)} pcs
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* First Row - Total Items and Bar Chart */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* Total Items Card */}
                    <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
                                <FaFileInvoice className={`text-2xl ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                            </div>
                        </div>
                        <h3 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {tallyItemsStats.total}
                        </h3>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Total Tally Items
                        </p>
                    </div>

                    {/* Items Created - Last 7 Days Bar Chart */}
                    <div style={chartStyle('items-trend')} className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                        <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Tally Items Created (Last 7 Days)
                        </h3>
                        <LazyMount style={{ height: '250px' }}>
                            <EChart
                                option={barOption({
                                    labels: tallyItemsStats.last7Days?.map(d => d.label) || [],
                                    series: [{
                                        name: 'Items Created',
                                        data: tallyItemsStats.last7Days?.map(d => d.count) || [],
                                        color: isDarkMode ? '#A855F7' : '#9333EA',
                                    }],
                                    isDark: isDarkMode,
                                })}
                            />
                        </LazyMount>
                    </div>
                </div>

                {/* Combined assignment polar area (brand / parameter / image combinations) */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div style={chartStyle('items-assignment')} className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                    <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Item Assignments
                    </h3>
                    {assignmentPieData.length > 0 ? (
                        <LazyMount style={{ height: '420px' }}>
                            <EChart
                                option={radialBarOption({
                                    data: assignmentPieData,
                                    isDark: isDarkMode,
                                    logScale: true,
                                })}
                            />
                        </LazyMount>
                    ) : (
                        <EmptyState message="No items" isDark={isDarkMode} className="py-12" />
                    )}
                </div>
                </div>
            </div>
            )}

            {/* Tally Ledger Management Section */}
            {hasTallyLedgerAccess && showSection('tallyLedger') && (
            <div className={sectionClass('tallyLedger')} style={sectionStyle('tallyLedger')}>
                <h2 className={`text-2xl font-bold mb-6 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <FaChartLine className="text-green-600" /> Tally Ledger Management
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Total Ledgers Card */}
                    <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-green-900/30' : 'bg-green-100'}`}>
                                <FaChartLine className={`text-2xl ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                            </div>
                        </div>
                        <h3 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {tallyLedgerStats.total}
                        </h3>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Total Ledger Entries
                        </p>
                    </div>

                    {/* Ledgers Created - Last 7 Days Bar Chart */}
                    <div style={chartStyle('ledger-trend')} className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                        <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Ledger Entries Created (Last 7 Days)
                        </h3>
                        <LazyMount style={{ height: '250px' }}>
                            <EChart
                                option={barOption({
                                    labels: tallyLedgerStats.last7Days?.map(d => d.label) || [],
                                    series: [{
                                        name: 'Ledgers Created',
                                        data: tallyLedgerStats.last7Days?.map(d => d.count) || [],
                                        color: isDarkMode ? '#22C55E' : '#16A34A',
                                    }],
                                    isDark: isDarkMode,
                                })}
                            />
                        </LazyMount>
                    </div>
                </div>
            </div>
            )}

            {/* New Pie Charts Section - 6 Charts: 3 for Created By Employee & 3 for Salesperson Distribution */}
            
            {/* Records by Type - Created By Employee Distribution (3 Charts) */}
            {showSection('personDistribution') && (
            <div className={sectionClass('personDistribution')} style={sectionStyle('personDistribution')}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div style={chartStyle('person-combined')} className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border p-6`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Person Distribution</h3>
                    <div className="flex flex-wrap items-center gap-2">
                        <SearchInput
                            value={personSearch}
                            onChange={(e) => setPersonSearch(e.target.value)}
                            onClear={() => setPersonSearch('')}
                            placeholder="Search person…"
                            isDark={isDarkMode}
                            className="w-full sm:w-56"
                        />
                        <SegmentedTabs
                            size="sm"
                            isDark={isDarkMode}
                            value={personRole}
                            onChange={setPersonRole}
                            options={[{ key: 'createdBy', label: 'Created By' }, { key: 'salesman', label: 'Salesperson' }]}
                        />
                    </div>
                </div>
                {PERSON_MODULES.length > 1 && (
                    <SegmentedTabs
                        size="sm"
                        className="mb-3"
                        isDark={isDarkMode}
                        value={activePersonModule}
                        onChange={setPersonModule}
                        options={PERSON_MODULES.map(m => ({ key: m.key, label: m.label }))}
                    />
                )}
                {personFiltered.length > 0 ? (
                    <LazyMount style={{ height: `${personChartHeight}px` }}>
                        <EChart
                            option={barOption({
                                labels: personFiltered.map(p => p.name),
                                series: personBarSeries,
                                isDark: isDarkMode,
                                horizontal: true,
                                tooltipFormatter: formatPersonTooltip,
                            })}
                        />
                    </LazyMount>
                ) : (
                    <EmptyState message="No data for this period" isDark={isDarkMode} className="py-12" />
                )}
            </div>
            </div>
            </div>
            )}

            {/* Recent Records Section */}
            {showSection('recentRecords') && (
            <div className={`space-y-6 ${sectionClass('recentRecords')}`} style={sectionStyle('recentRecords')}>
                {/* Tabs */}
                {recentTabs.length > 1 && (
                <SegmentedTabs
                    isDark={isDarkMode}
                    className="flex-wrap"
                    value={activeRecentTab}
                    onChange={setRecentTab}
                    options={recentTabs.map(tab => ({
                        key: tab.key,
                        label: tab.label,
                        badge: tab.key === 'delivery' ? deliveryOrderTotal : tab.key === 'quotation' ? quotationTotal : tallyQuotationTotal,
                    }))}
                />
                )}

                {/* Recent Delivery Orders */}
                {activeRecentTab === 'delivery' && hasDeliveryOrderAccess && (
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border overflow-hidden`}>
                    <div className="p-4 sm:p-6 border-b border-gray-700">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                            <div>
                                <h3 className={`text-base sm:text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                    Recent Delivery Orders
                                </h3>
                                <span className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    Total: {deliveryOrderTotal}
                                </span>
                            </div>
                            {/* Search Input with Apply Button */}
                            <div className="w-full sm:w-auto flex gap-2">
                                <SearchInput
                                    value={deliveryOrderSearchTemp}
                                    onChange={(e) => setDeliveryOrderSearchTemp(e.target.value)}
                                    onClear={() => setDeliveryOrderSearchTemp('')}
                                    placeholder="Search DO, recipient, creator, salesperson…"
                                    isDark={isDarkMode}
                                    className="flex-1 sm:min-w-[280px]"
                                />
                            </div>
                        </div>
                    </div>
                    
                    {/* Mobile Card View */}
                    <div className="block md:hidden">
                        {deliveryOrders.length === 0 ? (
                            <div className={`p-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                No delivery orders found{deliveryOrderSearch ? ` matching “${deliveryOrderSearch}”` : ''}
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-700">
                                {deliveryOrders.map((order) => (
                                    <div key={order._id} className={`p-4 ${isDarkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'} transition-colors`}>
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <div className={`text-sm font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                                    {order.doNumber}
                                                </div>
                                                <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                                    {new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} • {new Date(order.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                </div>
                                            </div>
                                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                order.status === 'Completed' 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : order.status === 'Pending' 
                                                    ? 'bg-yellow-100 text-yellow-800' 
                                                    : 'bg-red-100 text-red-800'
                                            }`}>
                                                {order.status}
                                            </span>
                                        </div>
                                        <div className="space-y-2 mb-3">
                                            <div className="flex justify-between text-sm">
                                                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Recipient:</span>
                                                <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} truncate ml-2`}>
                                                    {order.recipient?.name || order.recipient?.businessName || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created By:</span>
                                                <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                                    {order.createdBy?.name || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Amount:</span>
                                                <span className={`font-semibold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                                    {formatCurrency(order.totalAmountWithGST || order.totalAmountWithoutGST || 0)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleViewRecord(order, 'delivery')}
                                                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                                    isDarkMode 
                                                        ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                                                        : 'bg-indigo-500 hover:bg-indigo-600 text-white'
                                                }`}
                                            >
                                                View Details
                                            </button>
                                            <div className="relative group">
                                                <button
                                                    title="Print"
                                                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                                        isDarkMode 
                                                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                                                            : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                                                    }`}
                                                >
                                                    <FaPrint />
                                                </button>
                                                <div className={`absolute right-0 mt-1 w-44 rounded-md shadow-lg z-50 hidden group-hover:block ${
                                                    isDarkMode ? 'bg-gray-700 border border-gray-600' : 'bg-white border border-gray-200'
                                                }`}>
                                                    <button
                                                        onClick={() => window.open('/delivery-orders/' + order._id, '_blank')}
                                                        className={`block w-full text-left px-4 py-2 text-sm rounded-t-md ${
                                                            isDarkMode ? 'text-gray-200 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-100'
                                                        }`}
                                                    >
                                                        Print
                                                    </button>
                                                    <button
                                                        onClick={() => window.open('/delivery-orders-details/' + order._id, '_blank')}
                                                        className={`block w-full text-left px-4 py-2 text-sm rounded-b-md ${
                                                            isDarkMode ? 'text-gray-200 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-100'
                                                        }`}
                                                    >
                                                        Print with Discount
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto -mx-4 sm:mx-0">
                        <div className="inline-block min-w-full align-middle">
                            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className={isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}>
                                        <tr>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                DO Number
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Date
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Recipient
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider hidden lg:table-cell`}>
                                                Created By
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Total Amount
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Status
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                            <tbody className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                {deliveryOrders.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className={`px-3 sm:px-6 py-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            No delivery orders found{deliveryOrderSearch ? ` matching “${deliveryOrderSearch}”` : ''}
                                        </td>
                                    </tr>
                                ) : (
                                    deliveryOrders.map((order) => (
                                        <tr key={order._id} className={`${isDarkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'} transition-colors`}>
                                            <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                                {order.doNumber}
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                                <div>{new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                                    {new Date(order.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                </div>
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                                <div className="max-w-xs truncate">{order.recipient?.name || order.recipient?.businessName || 'N/A'}</div>
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} hidden lg:table-cell`}>
                                                {order.createdBy?.name || 'N/A'}
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-semibold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                                {formatCurrency(order.totalAmountWithGST || order.totalAmountWithoutGST || 0)}
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                    order.status === 'Completed' 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : order.status === 'Pending' 
                                                        ? 'bg-yellow-100 text-yellow-800' 
                                                        : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {order.status}
                                                </span>
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleViewRecord(order, 'delivery')}
                                                        className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                                                            isDarkMode 
                                                                ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                                                                : 'bg-indigo-500 hover:bg-indigo-600 text-white'
                                                        }`}
                                                    >
                                                        View
                                                    </button>
                                                    <div className="relative group">
                                                        <button
                                                            title="Print"
                                                            className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                                                                isDarkMode 
                                                                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                                                                    : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                                                            }`}
                                                        >
                                                            <FaPrint />
                                                        </button>
                                                        <div className={`absolute right-0 mt-1 w-44 rounded-md shadow-lg z-50 hidden group-hover:block ${
                                                            isDarkMode ? 'bg-gray-700 border border-gray-600' : 'bg-white border border-gray-200'
                                                        }`}>
                                                            <button
                                                                onClick={() => window.open('/delivery-orders/' + order._id, '_blank')}
                                                                className={`block w-full text-left px-4 py-2 text-sm rounded-t-md ${
                                                                    isDarkMode ? 'text-gray-200 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-100'
                                                                }`}
                                                            >
                                                                Print
                                                            </button>
                                                            <button
                                                                onClick={() => window.open('/delivery-orders-details/' + order._id, '_blank')}
                                                                className={`block w-full text-left px-4 py-2 text-sm rounded-b-md ${
                                                                    isDarkMode ? 'text-gray-200 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-100'
                                                                }`}
                                                            >
                                                                Print with Discount
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                            </div>
                        </div>
                    </div>
                    <PaginationControls 
                        currentPage={deliveryOrderPage}
                        totalItems={deliveryOrderTotal}
                        onPageChange={setDeliveryOrderPage}
                    />
                </div>
                )}

                {/* Recent Quotations */}
                {activeRecentTab === 'quotation' && hasQuotationNewAccess && (
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border overflow-hidden`}>
                    <div className="p-4 sm:p-6 border-b border-gray-700">
                        <div className="flex flex-col gap-3 sm:gap-4">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                                <div>
                                    <h3 className={`text-base sm:text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                        Recent Quotations
                                    </h3>
                                    <span className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Total: {quotationTotal}
                                    </span>
                                </div>
                            </div>
                            {/* Search and Date Range Filters */}
                            <div className="flex flex-col gap-2">
                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                    {/* Search Input */}
                                    <SearchInput
                                        value={quotationSearchTemp}
                                        onChange={(e) => setQuotationSearchTemp(e.target.value)}
                                        onClear={() => setQuotationSearchTemp('')}
                                        placeholder="Search quotation, recipient, creator, salesperson…"
                                        isDark={isDarkMode}
                                        className="flex-1 sm:min-w-[200px]"
                                    />
                                    {/* Date Range */}
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <input
                                            type="date"
                                            placeholder="Start Date"
                                            value={quotationDateRangeTemp.startDate}
                                            onChange={(e) => setQuotationDateRangeTemp(prev => ({ ...prev, startDate: e.target.value }))}
                                            className={`px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                isDarkMode 
                                                    ? 'bg-gray-700 border-gray-600 text-white' 
                                                    : 'bg-white border-gray-300 text-gray-900'
                                            }`}
                                        />
                                        <input
                                            type="date"
                                            placeholder="End Date"
                                            value={quotationDateRangeTemp.endDate}
                                            onChange={(e) => setQuotationDateRangeTemp(prev => ({ ...prev, endDate: e.target.value }))}
                                            className={`px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                isDarkMode 
                                                    ? 'bg-gray-700 border-gray-600 text-white' 
                                                    : 'bg-white border-gray-300 text-gray-900'
                                            }`}
                                        />
                                    </div>
                                </div>
                                {/* Action Buttons */}
                                <div className="flex gap-2 justify-end">
                                    <button
                                        onClick={handleApplyQuotationFilters}
                                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                            isDarkMode 
                                                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                    >
                                        Apply Filters
                                    </button>
                                    {(quotationSearch || quotationDateRange.startDate || quotationDateRange.endDate) && (
                                        <button
                                            onClick={handleClearQuotationFilters}
                                            className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                                                isDarkMode 
                                                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                        >
                                            Clear Filters
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Mobile Card View */}
                    <div className="block md:hidden">
                        {quotations.length === 0 ? (
                            <div className={`p-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                No quotations found{quotationSearch ? ` matching “${quotationSearch}”` : ''}
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-700">
                                {quotations.map((quote) => (
                                    <div key={quote._id} className={`p-4 ${isDarkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'} transition-colors`}>
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <div className={`text-sm font-semibold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                                    {quote.id || quote._id}
                                                </div>
                                                <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                                    {new Date(quote.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} • {new Date(quote.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                </div>
                                            </div>
                                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                quote.status === 'Approved' 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : quote.status === 'Pending' 
                                                    ? 'bg-yellow-100 text-yellow-800' 
                                                    : 'bg-red-100 text-red-800'
                                            }`}>
                                                {quote.status || 'Pending'}
                                            </span>
                                        </div>
                                        <div className="space-y-2 mb-3">
                                            <div className="flex justify-between text-sm">
                                                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Recipient:</span>
                                                <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} truncate ml-2`}>
                                                    {quote.recipient?.name || quote.recipient?.businessName || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created By:</span>
                                                <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                                    {quote.createdBy?.name || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Amount:</span>
                                                <span className={`font-semibold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                                    {formatCurrency(quote.totalValue || quote.totalAmount || 0)}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleViewRecord(quote, 'quotation')}
                                            className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                                isDarkMode 
                                                    ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                                                    : 'bg-purple-500 hover:bg-purple-600 text-white'
                                            }`}
                                        >
                                            View Details
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto -mx-4 sm:mx-0">
                        <div className="inline-block min-w-full align-middle">
                            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className={isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}>
                                        <tr>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Quotation ID
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Date
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Recipient
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider hidden lg:table-cell`}>
                                                Created By
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Total Amount
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Status
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                            <tbody className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                {quotations.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className={`px-3 sm:px-6 py-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            No quotations found{quotationSearch ? ` matching “${quotationSearch}”` : ''}
                                        </td>
                                    </tr>
                                ) : (
                                    quotations.map((quote) => (
                                        <tr key={quote._id} className={`${isDarkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'} transition-colors`}>
                                            <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                                {quote.id || quote._id}
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                                <div>{new Date(quote.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                                    {new Date(quote.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                </div>
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                                <div className="max-w-xs truncate">{quote.recipient?.name || quote.recipient?.businessName || 'N/A'}</div>
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} hidden lg:table-cell`}>
                                                {quote.createdBy?.name || 'N/A'}
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-semibold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                                {formatCurrency(quote.totalValue || quote.totalAmount || 0)}
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                    quote.status === 'Approved' 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : quote.status === 'Pending' 
                                                        ? 'bg-yellow-100 text-yellow-800' 
                                                        : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {quote.status || 'Pending'}
                                                </span>
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm">
                                                <button
                                                    onClick={() => handleViewRecord(quote, 'quotation')}
                                                    className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                                                        isDarkMode 
                                                            ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                                                            : 'bg-purple-500 hover:bg-purple-600 text-white'
                                                    }`}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                            </div>
                        </div>
                    </div>
                    <PaginationControls 
                        currentPage={quotationPage}
                        totalItems={quotationTotal}
                        onPageChange={setQuotationPage}
                    />
                </div>
                )}

                {/* Recent Tally Quotations */}
                {activeRecentTab === 'tally' && hasTallyQuotationAccess && (
                <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-lg border overflow-hidden`}>
                    <div className="p-4 sm:p-6 border-b border-gray-700">
                        <div className="flex flex-col gap-3 sm:gap-4">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                                <div>
                                    <h3 className={`text-base sm:text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                        Recent Tally Quotations
                                    </h3>
                                    <span className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Total: {tallyQuotationTotal}
                                    </span>
                                </div>
                            </div>
                            {/* Search and Date Range Filters */}
                            <div className="flex flex-col gap-2">
                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                    {/* Search Input */}
                                    <SearchInput
                                        value={tallyQuotationSearchTemp}
                                        onChange={(e) => setTallyQuotationSearchTemp(e.target.value)}
                                        onClear={() => setTallyQuotationSearchTemp('')}
                                        placeholder="Search quotation, recipient, creator, salesperson…"
                                        isDark={isDarkMode}
                                        className="flex-1 sm:min-w-[200px]"
                                    />
                                    {/* Date Range */}
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <input
                                            type="date"
                                            placeholder="Start Date"
                                            value={tallyQuotationDateRangeTemp.startDate}
                                            onChange={(e) => setTallyQuotationDateRangeTemp(prev => ({ ...prev, startDate: e.target.value }))}
                                            className={`px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                isDarkMode 
                                                    ? 'bg-gray-700 border-gray-600 text-white' 
                                                    : 'bg-white border-gray-300 text-gray-900'
                                            }`}
                                        />
                                        <input
                                            type="date"
                                            placeholder="End Date"
                                            value={tallyQuotationDateRangeTemp.endDate}
                                            onChange={(e) => setTallyQuotationDateRangeTemp(prev => ({ ...prev, endDate: e.target.value }))}
                                            className={`px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                                isDarkMode 
                                                    ? 'bg-gray-700 border-gray-600 text-white' 
                                                    : 'bg-white border-gray-300 text-gray-900'
                                            }`}
                                        />
                                    </div>
                                </div>
                                {/* Action Buttons */}
                                <div className="flex gap-2 justify-end">
                                    <button
                                        onClick={handleApplyTallyQuotationFilters}
                                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                            isDarkMode 
                                                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                    >
                                        Apply Filters
                                    </button>
                                    {(tallyQuotationSearch || tallyQuotationDateRange.startDate || tallyQuotationDateRange.endDate) && (
                                        <button
                                            onClick={handleClearTallyQuotationFilters}
                                            className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                                                isDarkMode 
                                                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                        >
                                            Clear Filters
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Mobile Card View */}
                    <div className="block md:hidden">
                        {tallyQuotations.length === 0 ? (
                            <div className={`p-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                No tally quotations found{tallyQuotationSearch ? ` matching “${tallyQuotationSearch}”` : ''}
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-700">
                                {tallyQuotations.map((quote) => (
                                    <div key={quote._id} className={`p-4 ${isDarkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'} transition-colors`}>
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <div className={`text-sm font-semibold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                                    {quote.id || quote._id.slice(-6)}
                                                </div>
                                                <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                                    {new Date(quote.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} • {new Date(quote.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                </div>
                                            </div>
                                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                quote.status === 'PENDING'
                                                    ? 'bg-yellow-100 text-yellow-800' 
                                                    : quote.status === 'COMPLETED'
                                                    ? 'bg-green-100 text-green-800'
                                                    : quote.status === 'PARTIAL-COMPLETE'
                                                    ? 'bg-blue-100 text-blue-800'
                                                    : quote.status === 'REFFOLLOW'
                                                    ? 'bg-purple-100 text-purple-800'
                                                    : quote.status === 'CANCELLED'
                                                    ? 'bg-red-100 text-red-800'
                                                    : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                {quote.status === 'PARTIAL-COMPLETE' ? 'Partial' : 
                                                 quote.status === 'REFFOLLOW' ? 'Ref Follow' :
                                                 quote.status ? quote.status.charAt(0) + quote.status.slice(1).toLowerCase() : 'Pending'}
                                            </span>
                                        </div>
                                        <div className="space-y-2 mb-3">
                                            <div className="flex justify-between text-sm">
                                                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Recipient:</span>
                                                <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} truncate ml-2`}>
                                                    {quote.recipient?.name || quote.recipient?.businessName || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created By:</span>
                                                <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                                    {quote.createdBy?.name || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Amount:</span>
                                                <span className={`font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                                    {formatCurrency(quote.totalAmount || quote.totalValue || 0)}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleViewRecord(quote, 'tally')}
                                            className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                                isDarkMode 
                                                    ? 'bg-teal-600 hover:bg-teal-700 text-white' 
                                                    : 'bg-teal-500 hover:bg-teal-600 text-white'
                                            }`}
                                        >
                                            View Details
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto -mx-4 sm:mx-0">
                        <div className="inline-block min-w-full align-middle">
                            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className={isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}>
                                        <tr>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Quote Number
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Date
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Recipient
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider hidden lg:table-cell`}>
                                                Created By
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Total Amount
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Status
                                            </th>
                                            <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wider`}>
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                            <tbody className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                {tallyQuotations.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className={`px-3 sm:px-6 py-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            No tally quotations found{tallyQuotationSearch ? ` matching “${tallyQuotationSearch}”` : ''}
                                        </td>
                                    </tr>
                                ) : (
                                    tallyQuotations.map((quote) => (
                                        <tr key={quote._id} className={`${isDarkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'} transition-colors`}>
                                            <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                                {quote.id || quote._id.slice(-6)}
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                                <div>{new Date(quote.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                                    {new Date(quote.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                </div>
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                                <div className="max-w-xs truncate">{quote.recipient?.name || quote.recipient?.businessName || 'N/A'}</div>
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} hidden lg:table-cell`}>
                                                {quote.createdBy?.name || 'N/A'}
                                            </td>
                                            <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                                {formatCurrency(quote.totalAmount || quote.totalValue || 0)}
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                    quote.status === 'PENDING'
                                                        ? 'bg-yellow-100 text-yellow-800' 
                                                        : quote.status === 'COMPLETED'
                                                        ? 'bg-green-100 text-green-800'
                                                        : quote.status === 'PARTIAL-COMPLETE'
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : quote.status === 'REFFOLLOW'
                                                        ? 'bg-purple-100 text-purple-800'
                                                        : quote.status === 'CANCELLED'
                                                        ? 'bg-red-100 text-red-800'
                                                        : quote.status === 'REJECTED'
                                                        ? 'bg-gray-100 text-gray-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {quote.status === 'PARTIAL-COMPLETE' ? 'Partial Complete' : 
                                                     quote.status === 'REFFOLLOW' ? 'Ref Follow' :
                                                     quote.status ? quote.status.charAt(0) + quote.status.slice(1).toLowerCase() : 'Pending'}
                                                </span>
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm">
                                                <button
                                                    onClick={() => handleViewRecord(quote, 'tally')}
                                                    className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                                                        isDarkMode 
                                                            ? 'bg-teal-600 hover:bg-teal-700 text-white' 
                                                            : 'bg-teal-500 hover:bg-teal-600 text-white'
                                                    }`}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                            </div>
                        </div>
                    </div>
                    <PaginationControls 
                        currentPage={tallyQuotationPage}
                        totalItems={tallyQuotationTotal}
                        onPageChange={setTallyQuotationPage}
                    />
                </div>
                )}
            </div>
            )}

            </div>
            {/* end sections grid */}

            {/* View Details Modal - Tally Quotation (exact copy from TallyQuotation.js) */}
            {showModal && selectedRecord && selectedRecord.type === 'tally' && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl w-full max-w-6xl flex flex-col h-[90vh] shadow-2xl animate-fadeIn`}>
                        {/* Header */}
                        <div className="bg-gradient-to-r from-indigo-600 to-blue-700 px-6 py-4 flex justify-between items-center flex-shrink-0 rounded-t-xl">
                            <div className="flex items-center space-x-4">
                                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white">Quotation {formatId(selectedRecord?.id)}</h2>
                                    <p className="text-xs text-blue-100 opacity-80">Created on {new Date(selectedRecord.createdAt).toLocaleDateString()}</p>
                                </div>
                            </div>
                            <button 
                                onClick={closeModal}
                                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                                aria-label="Close dialog"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Content - Scrollable */}
                        <div className="overflow-y-auto flex-grow">
                            {/* Status Bar */}
                            <div className={`${isDarkMode ? 'bg-gradient-to-r from-gray-700 to-gray-600 border-gray-600' : 'bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200'} px-6 py-3 sticky top-0 z-10 shadow-sm border-b flex items-center justify-between`}>
                                <div className="flex items-center space-x-4">
                                    <span className="bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-0.5 rounded-full flex items-center">
                                        <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                                        Active
                                    </span>
                                    <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-500'} text-sm`}>
                                        Total Items: {selectedRecord.sections?.reduce((acc, section) => acc + section.items.length, 0) || 0}
                                    </span>
                                </div>
                                <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                    Prepared by: {selectedRecord.createdBy?.name || 'Unknown'}
                                </div>
                            </div>

                            <div className="p-6 space-y-8">
                                {/* Overview Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {/* Recipient Card */}
                                    <div className={`${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} rounded-lg border shadow-sm overflow-hidden`}>
                                        <div className={`px-4 py-3 ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-50 border-gray-200'} border-b`}>
                                            <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} uppercase tracking-wide flex items-center`}>
                                                <svg className={`w-4 h-4 mr-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                                                </svg>
                                                Recipient
                                            </h3>
                                        </div>
                                        <div className="p-4">
                                            <div className={`mb-3 pb-3 border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-100'}`}>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Type</div>
                                                <div className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{selectedRecord.recipientType}</div>
                                            </div>
                                            <div>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Name</div>
                                                <div className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                                    {selectedRecord.recipientType === 'Customer' 
                                                        ? selectedRecord.recipient?.name 
                                                        : selectedRecord.recipient?.businessName}
                                                </div>
                                                {selectedRecord.recipient?.phone && (
                                                    <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                                                        {selectedRecord.recipient?.phone}
                                                    </div>
                                                )}
                                            </div>
                                            {selectedRecord.siteAddress && (
                                                <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-100'}`}>
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Site Address</div>
                                                    <div className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'} whitespace-pre-line`}>
                                                        {selectedRecord.siteAddress}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Amount Card */}
                                    <div className={`${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} rounded-lg border shadow-sm overflow-hidden`}>
                                        <div className={`px-4 py-3 ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-50 border-gray-200'} border-b`}>
                                            <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} uppercase tracking-wide flex items-center`}>
                                                <svg className={`w-4 h-4 mr-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                                Amount Details
                                            </h3>
                                        </div>
                                        <div className="p-4 space-y-3">
                                            <div className="flex justify-between items-center">
                                                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Listed Price:</div>
                                                <div className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>₹{formatNumberWithCommas(selectedRecord.listedPriceGrandTotal || 0)}</div>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Offer Price:</div>
                                                <div className={`font-medium ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>₹{formatNumberWithCommas(selectedRecord.offerPriceGrandTotal || 0)}</div>
                                            </div>
                                            <div className={`flex justify-between items-center pt-2 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-100'}`}>
                                                <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>Total Amount:</div>
                                                <div className={`text-lg font-bold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>₹{formatNumberWithCommas(selectedRecord.totalAmount || 0)}</div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Salesperson Card */}
                                    <div className={`${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} rounded-lg border shadow-sm overflow-hidden`}>
                                        <div className={`px-4 py-3 ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-50 border-gray-200'} border-b`}>
                                            <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} uppercase tracking-wide flex items-center`}>
                                                <svg className={`w-4 h-4 mr-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                                                </svg>
                                                Team
                                            </h3>
                                        </div>
                                        <div className="p-4">
                                            <div className="space-y-3">
                                                <div>
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Salesperson</div>
                                                    {selectedRecord.salesman && selectedRecord.salesman.length > 0 ? (
                                                        <div className="flex flex-wrap gap-2">
                                                            {selectedRecord.salesman.map((salesman) => (
                                                                <div key={salesman._id} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>
                                                                    {salesman.name}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className={`text-sm italic ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No salesperson assigned</div>
                                                    )}
                                                </div>
                                                
                                                <div>
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Reference</div>
                                                    <div className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                                        {selectedRecord.reference?.name || 'Not specified'}
                                                    </div>
                                                </div>
                                                
                                                <div>
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Transporter</div>
                                                    <div className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                                        {selectedRecord.transporterName?.name || 'Not specified'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Charges */}
                                <div className={`${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} rounded-lg border shadow-sm overflow-hidden`}>
                                    <div className={`px-4 py-3 ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-50 border-gray-200'} border-b`}>
                                        <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} uppercase tracking-wide flex items-center`}>
                                            <svg className={`w-4 h-4 mr-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                            </svg>
                                            Additional Charges
                                        </h3>
                                    </div>
                                    
                                    <div className="p-4">
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            <div className={`${isDarkMode ? 'bg-gray-600' : 'bg-gray-50'} rounded p-3`}>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Cartage</div>
                                                <div className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>₹{formatNumberWithCommas(selectedRecord.cartage || 0)}</div>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>+GST: ₹{formatNumberWithCommas((selectedRecord.cartage || 0) * 0.18)}</div>
                                            </div>
                                            
                                            <div className={`${isDarkMode ? 'bg-gray-600' : 'bg-gray-50'} rounded p-3`}>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Packing & Forwarding</div>
                                                <div className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>₹{formatNumberWithCommas(selectedRecord.packingAndForwarding || 0)}</div>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>+GST: ₹{formatNumberWithCommas((selectedRecord.packingAndForwarding || 0) * 0.18)}</div>
                                            </div>
                                            
                                            <div className={`${isDarkMode ? 'bg-gray-600' : 'bg-gray-50'} rounded p-3`}>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Labour</div>
                                                <div className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>₹{formatNumberWithCommas(selectedRecord.labour || 0)}</div>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>+GST: ₹{formatNumberWithCommas((selectedRecord.labour || 0) * 0.18)}</div>
                                            </div>
                                            
                                            <div className={`${isDarkMode ? 'bg-gray-600' : 'bg-gray-50'} rounded p-3`}>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Round Off</div>
                                                <div className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>₹{formatNumberWithCommas(selectedRecord.roundOff || 0)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Remarks */}
                                {selectedRecord.remarks && (
                                    <div className={`${isDarkMode ? 'bg-yellow-900 border-yellow-700' : 'bg-yellow-50 border-yellow-200'} border rounded-lg p-4`}>
                                        <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'} flex items-center mb-2`}>
                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                            </svg>
                                            Remarks
                                        </h3>
                                        <p className={`text-sm ${isDarkMode ? 'text-yellow-200' : 'text-yellow-800'}`}>{selectedRecord.remarks}</p>
                                    </div>
                                )}

                                {/* Sections */}
                                <div className="space-y-6">
                                    <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-100 border-gray-600' : 'text-gray-900 border-gray-200'} border-b pb-2`}>Quotation Items</h3>
                                    
                                    <div className="space-y-6">
                                        {selectedRecord.sections && selectedRecord.sections.map((section, sectionIndex) => (
                                            <div key={sectionIndex} className={`${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} border rounded-lg overflow-hidden shadow-sm`}>
                                                <div className={`${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200'} px-4 py-3 border-b flex justify-between items-center`}>
                                                    <h4 className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-800'} text-sm md:text-base`}>
                                                        Section {sectionIndex + 1}: {section.name || 'Untitled Section'}
                                                    </h4>
                                                    <span className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                                                        ₹{formatNumberWithCommas(section.offerPriceTotal || 0)}
                                                    </span>
                                                </div>
                                                
                                                <div className="overflow-x-auto -mx-4 sm:mx-0">
                                                    <div className="inline-block min-w-full align-middle">
                                                    <table className={`min-w-full divide-y ${isDarkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
                                                        <thead className={isDarkMode ? 'bg-gray-600' : 'bg-gray-50'}>
                                                            <tr>
                                                                <th scope="col" className={`px-2 sm:px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Item</th>
                                                                <th scope="col" className={`px-2 sm:px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider hidden md:table-cell`}>Application Area</th>
                                                                <th scope="col" className={`px-2 sm:px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Quantity</th>
                                                                <th scope="col" className={`px-2 sm:px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider hidden sm:table-cell`}>Price</th>
                                                                <th scope="col" className={`px-2 sm:px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider hidden md:table-cell`}>Discount</th>
                                                                <th scope="col" className={`px-2 sm:px-4 py-3 text-right text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Total</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className={`${isDarkMode ? 'bg-gray-700 divide-gray-600' : 'bg-white divide-gray-100'} divide-y`}>
                                                            {section.items && section.items.map((item, itemIndex) => (
                                                                <tr key={itemIndex} className={isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-50'}>
                                                                    <td className="px-2 sm:px-4 py-3">
                                                                        <div className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} text-sm max-w-xs truncate`}>{item.customName || item.itemName}</div>
                                                                        {item.item?.Name && item.customName && item.customName !== item.item.Name && (
                                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>{item.item.Name}</div>
                                                                        )}
                                                                    </td>
                                                                    <td className={`px-2 sm:px-4 py-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} hidden md:table-cell`}>
                                                                        {item.applicationArea || '-'}
                                                                    </td>
                                                                    <td className="px-2 sm:px-4 py-3">
                                                                        {(item.parameter?.calculationType === 'pcsPerBox' || (Number(item.boxes) > 0 || Number(item.pieces) > 0)) ? (
                                                                            <div className={`text-sm ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                                                                <span className="font-medium">{item.boxes}</span> Box, <span className="font-medium">{item.pieces}</span> Pcs
                                                                                {item.coverageArea > 0 && (
                                                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                                                                                        Coverage: {item.coverageArea} {item.coverageAreaUnit || 'sqft'}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <div className={`text-sm ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                                                                {item.quantity} {item.unit || 'pcs'}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    <td className={`px-2 sm:px-4 py-3 text-sm ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} hidden sm:table-cell`}>
                                                                        ₹{formatNumberWithCommas(item.listedPrice)}
                                                                    </td>
                                                                    <td className="px-2 sm:px-4 py-3 hidden md:table-cell">
                                                                        {item.discount > 0 ? (
                                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'}`}>
                                                                                {item.discount}%
                                                                            </span>
                                                                        ) : (
                                                                            <span className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>-</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-2 sm:px-4 py-3 text-right">
                                                                        <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                                                            ₹{formatNumberWithCommas(item.offerPriceTotal)}
                                                                        </div>
                                                                        {item.discount > 0 && (
                                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} line-through`}>
                                                                                ₹{formatNumberWithCommas(item.listedPriceTotal)}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot className={isDarkMode ? 'bg-gray-600' : 'bg-gray-50'}>
                                                            <tr>
                                                                <td colSpan="5" className={`px-2 sm:px-4 py-3 text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} text-right`}>
                                                                    Section Total:
                                                                </td>
                                                                <td className="px-2 sm:px-4 py-3 text-right">
                                                                    <div className={`font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                                                                        ₹{formatNumberWithCommas(section.offerPriceTotal)}
                                                                    </div>
                                                                    {section.offerPriceTotal !== section.listedPriceTotal && (
                                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} line-through`}>
                                                                            ₹{formatNumberWithCommas(section.listedPriceTotal)}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {/* Summary */}
                                    <div className={`${isDarkMode ? 'bg-gradient-to-r from-gray-700 to-gray-600 border-gray-500' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100'} rounded-lg p-4 border`}>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="space-y-1">
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Items</div>
                                                <div className={`text-xl font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                                                    {selectedRecord.sections?.reduce((total, section) => total + section.items.length, 0) || 0}
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Sections</div>
                                                <div className={`text-xl font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                                                    {selectedRecord.sections?.length || 0}
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Grand Total</div>
                                                <div className={`text-xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                                                    ₹{formatNumberWithCommas(selectedRecord.totalAmount || 0)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className={`border-t ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'} p-4 flex justify-end gap-3 flex-shrink-0 rounded-b-xl`}>
                            <button
                                onClick={closeModal}
                                className={`px-4 py-2 border rounded-lg flex items-center transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 ${isDarkMode ? 'border-gray-500 text-gray-200 bg-gray-600 hover:bg-gray-500' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Details Modal - Delivery Order & Quotation New */}
            {showModal && selectedRecord && (selectedRecord.type === 'delivery' || selectedRecord.type === 'quotation') && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-2 sm:p-4 backdrop-blur-sm">
                    <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl w-full max-w-7xl flex flex-col max-h-[95vh] sm:h-[90vh] shadow-2xl animate-fadeIn`}>
                        {/* Responsive Header */}
                        <div className={`${isDarkMode ? 'bg-gradient-to-r from-gray-700 to-gray-600' : 'bg-gradient-to-r from-indigo-600 to-blue-700'} px-3 sm:px-6 py-3 sm:py-4 flex justify-between items-start sm:items-center flex-shrink-0 rounded-t-xl`}>
                            <div className="flex items-start sm:items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
                                <div className="bg-white bg-opacity-20 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                                    <svg className="w-4 h-4 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h2 className="text-sm sm:text-xl font-bold text-white truncate">
                                        {selectedRecord.type === 'delivery' ? `Order Details: ${selectedRecord.doNumber}` :
                                         `Quotation #${selectedRecord.id || selectedRecord._id}`}
                                    </h2>
                                    <p className="text-xs text-blue-100 opacity-80 truncate">
                                        Created on {new Date(selectedRecord.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} by {selectedRecord.createdBy?.name || 'Unknown'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={closeModal}
                                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1.5 sm:p-2 transition-colors flex-shrink-0 ml-2"
                                aria-label="Close dialog"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Content - Scrollable with responsive padding */}
                        <div className="overflow-y-auto flex-grow">
                            <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
                                {/* Order Information & Recipient Information Grid (for Delivery Orders) */}
                                {selectedRecord.type === 'delivery' && (
                                    <>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                            {/* Order Information */}
                                            <div className={`${isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'} rounded-lg border p-3 sm:p-4`}>
                                                <div className="flex items-center mb-2 sm:mb-3">
                                                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    <h3 className={`text-xs sm:text-sm font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>Order Information</h3>
                                                </div>
                                                <div className="space-y-1.5 sm:space-y-2">
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>DO Number:</div>
                                                        <div className={`text-xs sm:text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} truncate`}>{selectedRecord.doNumber}</div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Status:</div>
                                                        <div>
                                                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                                                                selectedRecord.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                                                                selectedRecord.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                                                'bg-gray-100 text-gray-800'
                                                            }`}>
                                                                {selectedRecord.status}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created On:</div>
                                                        <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                            {new Date(selectedRecord.createdAt).toLocaleString('en-IN', { 
                                                                day: '2-digit', 
                                                                month: '2-digit', 
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                hour12: true
                                                            })}
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created By:</div>
                                                        <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                            {selectedRecord.createdBy?.name || 'Unknown'}
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Mode of Sales:</div>
                                                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.paymentMode || selectedRecord.modeOfSales || '-'}
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Manual DO:</div>
                                                        <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                            {selectedRecord.manualDoNumber || '-'}
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>PO Number:</div>
                                                        <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                            {selectedRecord.poNumber || '-'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Recipient Information */}
                                            <div className={`${isDarkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'} rounded-lg border p-4`}>
                                                <div className="flex items-center mb-3">
                                                    <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                    <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-purple-300' : 'text-purple-800'}`}>Recipient Information</h3>
                                                </div>
                                                <div className="space-y-2">
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Type:</div>
                                                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.recipientType || 'Customer'}
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Name:</div>
                                                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.recipient?.name || selectedRecord.recipient?.businessName || 'N/A'}
                                                        </div>
                                                    </div>
                                                    {selectedRecord.recipient?.mobileNumber && (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Phone:</div>
                                                            <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                                {selectedRecord.recipient.mobileNumber}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {selectedRecord.deliveryAddress && (
                                                        <div className="col-span-2">
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Delivery Address:</div>
                                                            <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} whitespace-pre-line`}>
                                                                {selectedRecord.deliveryAddress}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Team Information */}
                                            <div className={`${isDarkMode ? 'bg-gray-750 border-gray-600' : 'bg-white border-gray-200'} rounded-lg border p-4`}>
                                                <div className="flex items-center mb-3">
                                                    <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                    </svg>
                                                    <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-800'} uppercase tracking-wide`}>TEAM</h3>
                                                </div>
                                                <div className="space-y-2">
                                                    <div>
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Salesperson</div>
                                                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.salesman?.map(s => s.name).join(', ') || 
                                                             selectedRecord.salesman?.[0]?.name || 
                                                             selectedRecord.salesmanName ||
                                                             'Not specified'}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Reference</div>
                                                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.reference?.name || 
                                                             selectedRecord.referenceName ||
                                                             'Not specified'}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Transporter</div>
                                                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.transporterName?.name || 
                                                             selectedRecord.transporter?.name ||
                                                             selectedRecord.transporter ||
                                                             'Not specified'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Team Information (for Delivery Orders) - OLD VERSION TO REMOVE */}
                                {selectedRecord.type === 'delivery' && false && (
                                    <div className={`${isDarkMode ? 'bg-gray-750 border-gray-600' : 'bg-white border-gray-200'} rounded-lg border p-4`}>
                                        <div className="flex items-center mb-3">
                                            <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                            <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-800'} uppercase tracking-wide`}>TEAM</h3>
                                        </div>
                                        <div className="space-y-3">
                                            <div>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Salesperson</div>
                                                <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                    {selectedRecord.salesmen?.map(s => s.name).join(', ') || 
                                                     selectedRecord.salesmen?.[0]?.name || 
                                                     selectedRecord.salesmanName ||
                                                     'Not specified'}
                                                </div>
                                            </div>
                                            <div>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Reference</div>
                                                <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                    {selectedRecord.reference?.name || 
                                                     selectedRecord.referenceName ||
                                                     'Not specified'}
                                                </div>
                                            </div>
                                            <div>
                                                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Transporter</div>
                                                <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                    {selectedRecord.transporterName?.name || 
                                                     selectedRecord.transporter?.name ||
                                                     selectedRecord.transporter ||
                                                     'Not specified'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* For Quotation New - Enhanced with more details */}
                                {selectedRecord.type === 'quotation' && (
                                    <>
                                        {/* Quotation Information, Recipient Information & Team Grid */}
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {/* Quotation Information */}
                                            <div className={`${isDarkMode ? 'bg-indigo-900/20 border-indigo-700' : 'bg-indigo-50 border-indigo-200'} rounded-lg border p-4`}>
                                                <div className="flex items-center mb-3">
                                                    <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-indigo-300' : 'text-indigo-800'}`}>Quotation Information</h3>
                                                </div>
                                                <div className="space-y-2">
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Quotation ID:</div>
                                                        <div className={`text-sm font-bold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-700'}`}>
                                                            {formatId(selectedRecord.id) || selectedRecord._id?.slice(-6)}
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Status:</div>
                                                        <div>
                                                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                                                                selectedRecord.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                                                                selectedRecord.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                                                                selectedRecord.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                                                                selectedRecord.status === 'PARTIAL-COMPLETE' ? 'bg-blue-100 text-blue-800' :
                                                                selectedRecord.status === 'REFFOLLOW' ? 'bg-purple-100 text-purple-800' :
                                                                'bg-gray-100 text-gray-800'
                                                            }`}>
                                                                {selectedRecord.status || 'PENDING'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created On:</div>
                                                        <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                            {new Date(selectedRecord.createdAt).toLocaleString('en-IN', { 
                                                                day: '2-digit', 
                                                                month: '2-digit', 
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                hour12: true
                                                            })}
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created By:</div>
                                                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.createdBy?.name || 'Unknown'}
                                                        </div>
                                                    </div>
                                                    {selectedRecord.updatedAt && selectedRecord.updatedAt !== selectedRecord.createdAt && (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Last Updated:</div>
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                                                {new Date(selectedRecord.updatedAt).toLocaleString('en-IN', { 
                                                                    day: '2-digit', 
                                                                    month: 'short',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Items:</div>
                                                        <div className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.products?.length || 0}
                                                        </div>
                                                    </div>
                                                    {selectedRecord.sections && selectedRecord.sections.length > 0 && (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Sections:</div>
                                                            <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                                {selectedRecord.sections.length}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Recipient Information */}
                                            <div className={`${isDarkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'} rounded-lg border p-4`}>
                                                <div className="flex items-center mb-3">
                                                    <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                    <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-purple-300' : 'text-purple-800'}`}>Recipient Details</h3>
                                                </div>
                                                <div className="space-y-2">
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Type:</div>
                                                        <div>
                                                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                                                selectedRecord.recipientType === 'Customer' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                                                            }`}>
                                                                {selectedRecord.recipientType || 'Customer'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Name:</div>
                                                        <div className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.recipient?.name || selectedRecord.recipient?.businessName || 'N/A'}
                                                        </div>
                                                    </div>
                                                    {selectedRecord.recipient?.mobileNumber && (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Phone:</div>
                                                            <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                                {selectedRecord.recipient.mobileNumber}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {selectedRecord.recipient?.email && (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Email:</div>
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} truncate`}>
                                                                {selectedRecord.recipient.email}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {selectedRecord.recipient?.station?.name && (
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Station:</div>
                                                            <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                                {selectedRecord.recipient.station.name}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {selectedRecord.siteAddress && (
                                                        <div className="col-span-2 pt-2 border-t border-purple-300">
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Site Address:</div>
                                                            <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} whitespace-pre-line`} 
                                                                 style={{ maxHeight: '4.5rem', overflow: 'auto' }}>
                                                                {selectedRecord.siteAddress}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Team & Financial Summary */}
                                            <div className={`${isDarkMode ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'} rounded-lg border p-4`}>
                                                <div className="flex items-center mb-3">
                                                    <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                    </svg>
                                                    <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-green-300' : 'text-green-800'}`}>Team & Amount</h3>
                                                </div>
                                                <div className="space-y-2">
                                                    <div>
                                                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Salesperson</div>
                                                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {selectedRecord.salesmen?.length > 0 
                                                                ? selectedRecord.salesmen.map(s => s.name).join(', ')
                                                                : 'Not assigned'}
                                                        </div>
                                                    </div>
                                                    {selectedRecord.reference?.name && (
                                                        <div>
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Reference</div>
                                                            <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                                {selectedRecord.reference.name}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {selectedRecord.interior?.name && (
                                                        <div>
                                                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Interior Designer</div>
                                                            <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                                {selectedRecord.interior.name}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="pt-2 border-t border-green-300 space-y-1">
                                                        {selectedRecord.subTotal && (
                                                            <div className="flex justify-between items-center">
                                                                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Subtotal:</span>
                                                                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                                    ₹{formatNumberWithCommas(selectedRecord.subTotal)}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {selectedRecord.gstAmount && selectedRecord.gstAmount > 0 && (
                                                            <div className="flex justify-between items-center">
                                                                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>GST:</span>
                                                                <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                                    ₹{formatNumberWithCommas(selectedRecord.gstAmount)}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className="flex justify-between items-center pt-1 border-t border-green-400">
                                                            <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Grand Total:</span>
                                                            <span className={`text-lg font-bold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                                                                ₹{formatNumberWithCommas(selectedRecord.totalAmount || selectedRecord.totalValue || selectedRecord.grandTotal || 0)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Items Table - Comprehensive (matching screenshot) */}
                                {((selectedRecord.type === 'delivery' && selectedRecord.items && selectedRecord.items.length > 0) ||
                                  (selectedRecord.type === 'quotation' && selectedRecord.products && selectedRecord.products.length > 0)) && (
                                    <div className={`rounded-lg shadow-sm overflow-hidden border ${isDarkMode ? 'items-dark border-gray-700' : 'border-gray-200'}`}>
                                        <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-gray-700 to-gray-800 flex items-center border-b border-gray-600">
                                            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                            </svg>
                                            <h3 className="text-xs sm:text-sm font-semibold text-white uppercase tracking-wide">Items ({selectedRecord.type === 'delivery' ? selectedRecord.items?.length : selectedRecord.products?.length})</h3>
                                        </div>
                                        
                                        {/* Desktop Table View */}
                                        <div className="hidden md:block overflow-x-auto bg-white">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                {/* Different headers for delivery vs quotation */}
                                                {selectedRecord.type === 'delivery' ? (
                                                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                                                        <tr>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">ITEM</th>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider hidden xl:table-cell">PRINT TYPE</th>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider hidden 2xl:table-cell">DESCRIPTION</th>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">HSN</th>
                                                            <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">QTY</th>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">UNIT</th>
                                                            <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">RATE</th>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">DISCOUNT</th>
                                                            {user?.user?.isTT && (
                                                                <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider hidden xl:table-cell">TT</th>
                                                            )}
                                                            <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">AMOUNT</th>
                                                        </tr>
                                                    </thead>
                                                ) : (
                                                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                                                        <tr>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">ITEM NAME</th>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">HSN</th>
                                                            <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">QTY</th>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">UNIT</th>
                                                            <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider hidden lg:table-cell">LISTED PRICE</th>
                                                            <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">DISCOUNT</th>
                                                            <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">OFFER PRICE</th>
                                                            <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">AMOUNT</th>
                                                        </tr>
                                                    </thead>
                                                )}
                                                <tbody className="bg-white divide-y divide-gray-100">
                                                    {selectedRecord.products?.map((product, index) => {
                                                        // Skip if product is undefined or null
                                                        if (!product) return null;
                                                        
                                                        // For delivery orders, calculate TT amount from discount
                                                        const itemQty = product.quantity || 0;
                                                        const itemRate = product.rate || product.unitPrice || product.price || 0;
                                                        const itemDiscount = product.discount || 0;
                                                        const ttAmount = selectedRecord.type === 'delivery' 
                                                            ? (itemQty * itemRate * itemDiscount / 100) 
                                                            : (product.ttAmount || 0);
                                                        
                                                        // Render different columns based on type
                                                        if (selectedRecord.type === 'delivery') {
                                                            return (
                                                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                                                                <td className="px-3 py-3 text-sm font-medium text-gray-900">
                                                                    <div className="max-w-[200px] truncate">{product.item?.Name || product.item?.name || product.item?.productName || product.item?.itemName || product.Name || product.name || product.tileId?.name || 'N/A'}</div>
                                                                </td>
                                                                <td className="px-3 py-3 hidden xl:table-cell">
                                                                    {product.printType && (
                                                                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                                                            product.printType === 'item' ? 'bg-blue-100 text-blue-800' :
                                                                            product.printType === 'both' ? 'bg-green-100 text-green-800' :
                                                                            'bg-gray-100 text-gray-800'
                                                                        }`}>
                                                                            {product.printType}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td className="px-3 py-3 text-sm text-gray-600 hidden 2xl:table-cell">
                                                                    <div className="max-w-[150px] truncate">{product.description || product.itemDescription || '-'}</div>
                                                                </td>
                                                                <td className="px-3 py-3 text-sm text-gray-700">
                                                                    {product.hsn || product.hsnCode || '-'}
                                                                </td>
                                                                <td className="px-3 py-3 text-right text-sm font-medium text-gray-900">
                                                                    {formatNumberWithCommas(product.quantity || 0)}
                                                                </td>
                                                                <td className="px-3 py-3 text-sm text-gray-600">
                                                                    {product.units || product.unit || 'box'}
                                                                </td>
                                                                <td className="px-3 py-3 text-right text-sm text-gray-900">
                                                                    ₹{formatNumberWithCommas(product.rate || product.unitPrice || product.price || 0)}
                                                                </td>
                                                                <td className="px-3 py-3">
                                                                    {product.discount && parseFloat(product.discount) > 0 ? (
                                                                        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                                            {product.discount}%
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-sm text-gray-400">-</span>
                                                                    )}
                                                                </td>
                                                                {user?.user?.isTT && (
                                                                    <td className="px-3 py-3 text-right text-sm text-gray-700 hidden xl:table-cell">
                                                                        {ttAmount && parseFloat(ttAmount) > 0 ? 
                                                                            `₹${formatNumberWithCommas(ttAmount)}` : 
                                                                            '-'
                                                                        }
                                                                    </td>
                                                                )}
                                                                <td className="px-3 py-3 text-right text-sm font-bold text-gray-900">
                                                                    ₹{formatNumberWithCommas(product.amountAfterDiscount || product.totalPrice || product.amount || (product.quantity * (product.rate || product.unitPrice || product.price || 0)) || 0)}
                                                                </td>
                                                            </tr>
                                                            );
                                                        } else {
                                                            // Quotation row with descriptive pricing
                                                            const listedPrice = product.listedPrice || product.unitPrice || product.price || 0;
                                                            const offerPrice = product.rate || product.unitPrice || product.price || 0;
                                                            
                                                            return (
                                                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                                                                <td className="px-3 py-3 text-sm font-medium text-gray-900">
                                                                    <div className="max-w-[200px] truncate">{product.skuCode || product.item?.name || product.item?.productName || product.item?.itemName || product.name || 'N/A'}</div>
                                                                    {product.skuDescription && (
                                                                        <div className="text-xs mt-1 text-gray-600 max-w-[200px] truncate">
                                                                            {product.item.skuDescription}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td className="px-3 py-3 text-sm text-gray-700">
                                                                    {product.hsn || product.hsnCode || '-'}
                                                                </td>
                                                                <td className="px-3 py-3 text-right text-sm font-medium text-gray-900">
                                                                    {formatNumberWithCommas(product.quantity || 0)}
                                                                </td>
                                                                <td className="px-3 py-3 text-sm text-gray-600">
                                                                    {product.units || product.unit || 'pcs'}
                                                                </td>
                                                                <td className="px-3 py-3 text-right text-sm text-gray-500 line-through hidden lg:table-cell">
                                                                    {listedPrice > 0 ? `₹${formatNumberWithCommas(listedPrice)}` : '-'}
                                                                </td>
                                                                <td className="px-3 py-3">
                                                                    {product.discount && parseFloat(product.discount) > 0 ? (
                                                                        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                                            {product.discount}%
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-sm text-gray-400">-</span>
                                                                    )}
                                                                </td>
                                                                <td className="px-3 py-3 text-right text-sm font-bold text-blue-700">
                                                                    ₹{formatNumberWithCommas(offerPrice)}
                                                                </td>
                                                                <td className="px-3 py-3 text-right text-sm font-bold text-gray-900">
                                                                    ₹{formatNumberWithCommas(product.totalPrice || product.amount || (product.quantity * offerPrice) || 0)}
                                                                </td>
                                                            </tr>
                                                            );
                                                        }
                                                    })}
                                                </tbody>
                                                <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                                                    <tr>
                                                        {/* Adjust colspan based on delivery vs quotation and TT visibility */}
                                                        <td colSpan={
                                                            selectedRecord.type === 'delivery' 
                                                                ? (user?.user?.isTT ? "9" : "8")
                                                                : "7"
                                                        } className="px-3 py-3 text-right text-sm font-bold text-gray-700">
                                                            Total:
                                                        </td>
                                                        <td className="px-3 py-3 text-right text-base font-bold text-blue-700">
                                                            ₹{formatNumberWithCommas(
                                                                (selectedRecord.type === 'delivery' ? selectedRecord.items : selectedRecord.products)?.reduce((sum, p) => 
                                                                    sum + (p.amountAfterDiscount || p.totalPrice || p.amount || (p.quantity * (p.rate || p.unitPrice || p.price || 0)) || 0), 0
                                                                ) || 0
                                                            )}
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                        
                                        {/* Mobile/Tablet Card View */}
                                        <div className="md:hidden divide-y divide-gray-200 bg-white">
                                            {selectedRecord.products?.map((product, index) => {
                                                if (!product) return null;
                                                
                                                const itemQty = product.quantity || 0;
                                                const itemRate = product.rate || product.unitPrice || product.price || 0;
                                                const itemDiscount = product.discount || 0;
                                                const ttAmount = selectedRecord.type === 'delivery' 
                                                    ? (itemQty * itemRate * itemDiscount / 100) 
                                                    : (product.ttAmount || 0);
                                                
                                                return (
                                                    <div key={index} className="p-3 hover:bg-gray-50">
                                                        {/* Item Name */}
                                                        <div className="font-medium text-sm mb-2 text-gray-900">
                                                            {product.item?.Name || product.item?.name || product.skuCode || product.name || 'N/A'}
                                                        </div>
                                                        
                                                        {/* Item Details Grid */}
                                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                                            {selectedRecord.type === 'delivery' ? (
                                                                <>
                                                                    {product.printType && (
                                                                        <div>
                                                                            <span className="text-gray-600">Print Type:</span>
                                                                            <span className={`ml-1 inline-block px-2 py-0.5 rounded font-medium ${
                                                                                product.printType === 'item' ? 'bg-blue-100 text-blue-800' :
                                                                                product.printType === 'both' ? 'bg-green-100 text-green-800' :
                                                                                'bg-gray-100 text-gray-800'
                                                                            }`}>
                                                                                {product.printType}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    <div>
                                                                        <span className="text-gray-600">HSN:</span>
                                                                        <span className="ml-1 text-gray-900">
                                                                            {product.hsn || product.hsnCode || '-'}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-600">Qty:</span>
                                                                        <span className="ml-1 font-medium text-gray-900">
                                                                            {formatNumberWithCommas(product.quantity || 0)} {product.units || product.unit || 'box'}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-600">Rate:</span>
                                                                        <span className="ml-1 text-gray-900">
                                                                            ₹{formatNumberWithCommas(product.rate || product.unitPrice || product.price || 0)}
                                                                        </span>
                                                                    </div>
                                                                    {product.discount && parseFloat(product.discount) > 0 && (
                                                                        <div>
                                                                            <span className="text-gray-600">Discount:</span>
                                                                            <span className="ml-1 inline-block px-2 py-0.5 rounded font-medium bg-green-100 text-green-800">
                                                                                {product.discount}%
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {user?.user?.isTT && ttAmount && parseFloat(ttAmount) > 0 && (
                                                                        <div>
                                                                            <span className="text-gray-600">TT:</span>
                                                                            <span className="ml-1 text-gray-700">
                                                                                ₹{formatNumberWithCommas(ttAmount)}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div>
                                                                        <span className="text-gray-600">HSN:</span>
                                                                        <span className="ml-1 text-gray-900">
                                                                            {product.hsn || product.hsnCode || '-'}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-600">Qty:</span>
                                                                        <span className="ml-1 font-medium text-gray-900">
                                                                            {formatNumberWithCommas(product.quantity || 0)} {product.units || product.unit || 'pcs'}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-600">Listed Price:</span>
                                                                        <span className="ml-1 text-gray-600 line-through">
                                                                            ₹{formatNumberWithCommas(product.listedPrice || product.unitPrice || product.price || 0)}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-600">Offer Price:</span>
                                                                        <span className="ml-1 font-bold text-blue-700">
                                                                            ₹{formatNumberWithCommas(product.rate || product.unitPrice || product.price || 0)}
                                                                        </span>
                                                                    </div>
                                                                    {product.discount && parseFloat(product.discount) > 0 && (
                                                                        <div>
                                                                            <span className="text-gray-600">Discount:</span>
                                                                            <span className="ml-1 inline-block px-2 py-0.5 rounded font-medium bg-green-100 text-green-800">
                                                                                {product.discount}%
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                        
                                                        {/* Description (if available) */}
                                                        {product.description && selectedRecord.type === 'delivery' && (
                                                            <div className="mt-2 text-xs text-gray-600">
                                                                {product.description}
                                                            </div>
                                                        )}
                                                        
                                                        {/* Amount */}
                                                        <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between items-center">
                                                            <span className="text-xs font-medium text-gray-600">Amount:</span>
                                                            <span className="text-sm font-bold text-gray-900">
                                                                ₹{formatNumberWithCommas(product.amountAfterDiscount || product.totalPrice || product.amount || (product.quantity * (product.rate || product.unitPrice || product.price || 0)) || 0)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            
                                            {/* Mobile Total */}
                                            <div className="p-3 bg-gray-100 border-t-2 border-gray-300">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-bold text-gray-700">Total:</span>
                                                    <span className="text-base font-bold text-blue-700">
                                                        ₹{formatNumberWithCommas(
                                                            (selectedRecord.type === 'delivery' ? selectedRecord.items : selectedRecord.products)?.reduce((sum, p) => 
                                                                sum + (p.amountAfterDiscount || p.totalPrice || p.amount || (p.quantity * (p.rate || p.unitPrice || p.price || 0)) || 0), 0
                                                            ) || 0
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Additional Charges, Other Details & Amounts Grid */}
                                {selectedRecord.type === 'delivery' && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {/* Additional Charges */}
                                        <div className={`${isDarkMode ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'} rounded-lg border p-4`}>
                                            <div className="flex items-center mb-3">
                                                <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-green-300' : 'text-green-800'}`}>Additional Charges</h3>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>P&F Charges:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        ₹{formatNumberWithCommas(selectedRecord.packingAndForwarding || selectedRecord.pnfCharges || 0)}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Labour:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        ₹{formatNumberWithCommas(selectedRecord.labour || 0)}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Cartage:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        ₹{formatNumberWithCommas(selectedRecord.cartage || 0)}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Round Off:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        ₹{formatNumberWithCommas(selectedRecord.roundOff || 0)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Other Details */}
                                        <div className={`${isDarkMode ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'} rounded-lg border p-4`}>
                                            <div className="flex items-center mb-3">
                                                <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>Other Details</h3>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Vehicle Number:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        {selectedRecord.vehicleNumber || selectedRecord.vehicle?.vehicleNumber || '-'}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Transporter:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        {selectedRecord.transporterName?.name || selectedRecord.transporter || '-'}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Godown:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        {selectedRecord.godown?.name || selectedRecord.godownName || '-'}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Reference:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        {selectedRecord.reference?.name || '-'}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Payment Mode:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        {selectedRecord.paymentMode || selectedRecord.modeOfSales || '-'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Amounts */}
                                        <div className={`${isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'} rounded-lg border p-4`}>
                                            <div className="flex items-center mb-3">
                                                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                </svg>
                                                <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>Amounts</h3>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Amount without GST:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        ₹{formatNumberWithCommas(selectedRecord.totalAmountWithoutGST || selectedRecord.amountWithoutGST || 0)}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>GST Amount:</div>
                                                    <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        ₹{formatNumberWithCommas(
                                                            selectedRecord.gstAmount || 
                                                            (selectedRecord.totalAmountWithGST && selectedRecord.totalAmountWithoutGST ? 
                                                                selectedRecord.totalAmountWithGST - selectedRecord.totalAmountWithoutGST : 0)
                                                        )}
                                                    </div>
                                                </div>
                                                <div className={`grid grid-cols-2 gap-2 pt-2 ${isDarkMode ? 'border-t border-blue-700' : 'border-t border-blue-200'}`}>
                                                    <div className={`text-sm font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>Final Amount:</div>
                                                    <div className={`text-base font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                                                        ₹{formatNumberWithCommas(selectedRecord.totalAmountWithGST || selectedRecord.finalAmount || selectedRecord.totalAmount || 0)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Remarks */}
                                {selectedRecord.remarks && (
                                    <div className={`${isDarkMode ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'} border rounded-lg p-4`}>
                                        <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-800'} flex items-center mb-2`}>
                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                            </svg>
                                            Remarks
                                        </h3>
                                        <p className={`text-sm ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>{selectedRecord.remarks}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* End of Content - Scrollable */}

                    {/* Footer */}
                    <div className={`${isDarkMode ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'} border-t p-4 flex justify-end gap-3 flex-shrink-0 rounded-b-xl`}>
                        <button
                                onClick={closeModal}
                                className={`px-4 py-2 border ${isDarkMode ? 'border-gray-600 bg-gray-700 hover:bg-gray-600 text-white' : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-700'} rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${isDarkMode ? 'focus:ring-gray-500' : 'focus:ring-gray-500'} flex items-center transition-colors shadow-sm`}
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Dashboard;